import React from 'react';
import { Formik, Form, FieldArray, FormikErrors, FormikTouched } from 'formik';
import { validationSchema, initialValues, ServiceItem, getPackageListURL, PackageListResponse, PharmacyListResponse, getPharmacyListURL, getbillingConfigURL, billingConfigResponse, ServiceMasterItem, getServicesURL, PackageFormValues, PackageDetailsURL, getPackageDetailsURL, getOpenPackageServicesURL, PharmacyItem, Meta, distributeConcessionToServices, ProcedureDetailItem } from './packageFormHelpers';
import ServiceSummaryRow from './ServiceSummaryRow';
import { TextField, RadioGroup, FormControlLabel, Radio, Checkbox, Typography, Box, Button, Grid2, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Autocomplete, IconButton, Chip, Tooltip, Popover, List, ListItem, ListItemText } from '@mui/material';
import { ReactComponent as DeleteIcon } from '../../../../../assets/SVG/delete.svg';
import { getRequest, postRequest, putRequest } from '../../../../../services/httpService';
import { useAppQuery } from '../../../../../services/appQuery';
import {  usePatientContext } from '../../../../../components/SubPatinetProvider';
import { ConfirmationModal } from '../../../../../components/confirmationModal';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../../../../../components/alertProvider';
import PackageDetailsViewMode from './PackageDetails-ViewMode';
import { RequiredLabel } from '../../../../../utils/commonUtils';
import { getPackageDetails, PackageDefinitionFormValues } from '../../../../Configuration/Package/PackageDefinition.helper';
import { useTranslation } from 'react-i18next';
import { useCounselingStore } from '../../../store';
import styles from '../../../../Inventory/Common/Inventory.module.css';
import useCurrency from '../../../../../components/CurrencyProvider';
import { useLoader } from '../../../../../components/loaderProvider/loader';

// Helper component to sync derived Formik values via useEffect (avoids setState during render)
const FormValuesSync: React.FC<{
  values: PackageFormValues;
  setFieldValue: (field: string, value: unknown) => void;
}> = ({ values, setFieldValue }) => {
  // Sync package_net_amount
  React.useEffect(() => {
    const pkgAmt = Number(values.package_details?.package_amount) || 0;
    const distributedPackageConcession = Number(values.package_details.package_concession_amount) || 0;
    const totalExtraServiceConcession = (values.service_items || []).reduce((sum, si) => {
      const individualAmt = Number(si.individual_discount_amount) || 0;
      const qty = Number(si.quantity) || 0;
      return sum + (individualAmt * qty);
    }, 0);
    const totalConcessionDisplay = Number((distributedPackageConcession + totalExtraServiceConcession).toFixed(2));
    const netAmount = pkgAmt - totalConcessionDisplay;
    if (values.package_details.package_net_amount !== netAmount) {
      setFieldValue('package_details.package_net_amount', netAmount);
    }
  }, [values.package_details?.package_amount, values.package_details.package_concession_amount, values.service_items, setFieldValue, values.package_details.package_net_amount]);

  // Sync total_service_amount
  React.useEffect(() => {
    const totalServicesAmount = (values.service_items || []).reduce((sum, si) => {
      const rowGross = (Number(si.unit_rate) || 0) * (Number(si.quantity) || 0);
      return sum + rowGross;
    }, 0);
    const totalServicesFixed = Number(totalServicesAmount.toFixed(2));
    if (values.package_details.total_service_amount !== totalServicesFixed) {
      setFieldValue('package_details.total_service_amount', totalServicesFixed);
    }
  }, [values.service_items, setFieldValue, values.package_details.total_service_amount]);

  // Sync service item derived fields (gross_amount, discount_percent, total_amount, unit_rate_percentage)
  // Batched: compute all updates first, then apply in a single setFieldValue call
  React.useEffect(() => {
    const serviceItems = values.service_items || [];
    if (serviceItems.length === 0) return;

    const servAmt = Number(values.package_details?.service_amount) || 0;
    let hasChanges = false;
    const updatedItems = serviceItems.map((item) => {
      const unitRate = Number(item.unit_rate) || 0;
      const quantity = Number(item.quantity) || 0;
      const rawGrossAmount = unitRate * quantity;
      const fixedGross = Number(rawGrossAmount.toFixed(2));
      
      const storedDiscountAmount = Number(item.discount_amount) || 0;
      const effectiveDiscountPct = unitRate > 0 
        ? Number(((storedDiscountAmount / unitRate) * 100).toFixed(2)) 
        : 0;
      
      const finalDiscountAmount = Number((storedDiscountAmount * quantity).toFixed(2));
      const fixedTotal = Number((fixedGross - finalDiscountAmount).toFixed(2));
      
      // Calculate unit_rate_percentage based on service amount
      const calculatedUnitPercent = servAmt > 0 && quantity > 0 && unitRate > 0
        ? Number(((unitRate  * 100) / servAmt).toFixed(2))
        : 0;

      // Check if any field needs updating
      const grossChanged = item.gross_amount !== fixedGross;
      const pctChanged = Number(item.discount_percent) !== effectiveDiscountPct;
      const totalChanged = item.total_amount !== fixedTotal;
      const unitPctChanged = Number(item.unit_rate_percentage) !== calculatedUnitPercent;

      if (grossChanged || pctChanged || totalChanged || unitPctChanged) {
        hasChanges = true;
        return {
          ...item,
          gross_amount: fixedGross,
          discount_percent: effectiveDiscountPct,
          total_amount: fixedTotal,
          unit_rate_percentage: calculatedUnitPercent,
        };
      }
      return item;
    });

    // Only update if there are actual changes (single setFieldValue call)
    if (hasChanges) {
      setFieldValue('service_items', updatedItems);
    }
  }, [values.service_items, values.package_details?.service_amount, setFieldValue]);

  return null;
};

const CreatePackage: React.FC<{  patient_procedure?: number | null }> = ({  patient_procedure }) => {
  const storedCounselingSessionId = useCounselingStore((state) => state.counselingSessionId);
  // Initialize isViewMode to true if we have a session ID (editing existing package)
  // This prevents flash of edit form before view form loads
  const [isViewMode, setIsViewMode] = React.useState(Boolean(storedCounselingSessionId));
  const navigate = useNavigate();
  const { t: tBilling } = useTranslation('billing_config');
  const { t: tPackage } = useTranslation('Packagebilling');
  const { currencySymbol } = useCurrency();
  const { addAlert }  = useAlert();
  const { showLoader, hideLoader } = useLoader();
  const lastHandledIdRef = React.useRef<string | null>(null);
  const [submitPayload, setSubmitPayload] = React.useState<PackageFormValues | null>(null);
  const submissionId = submitPayload ? JSON.stringify(submitPayload) : '';
  const [activeTab, setActiveTab] = React.useState<'services' | 'pharmacy'>('services');
  const [inputValue, setInputValue] = React.useState('');
  const [inputValueForService, setInputValueForService] = React.useState('');
  const [pharmacySearch, setPharmacySearch] = React.useState('');
  const [serviceSearch, setServiceSearch] = React.useState('');
  const [selectedItemGroupCode, setSelectedItemGroupCode] = React.useState<string>('');
  const [selectedItemCategoryCode, setSelectedItemCategoryCode] = React.useState<string>('');
  const { patientId } = usePatientContext();
  type PharmacyItemType = PharmacyListResponse['objects'][0];
  const [selectedDrug, setSelectedDrug] = React.useState<PharmacyItemType | null>(null);
  const [selectedService, setSelectedService] = React.useState<ServiceMasterItem | null>(null);
  const [selectedTariffId, setSelectedTariffId] = React.useState<number | null>(null);
  const [selectedPackageId, setSelectedPackageId] = React.useState<number | null>(null);
  const [selectedBillingSource, setSelectedBillingSource] = React.useState<string>('');
  const [selectedTariffName, setSelectedTariffName] = React.useState<string>('');
  const [MetaForStatus, setMetaForStatus] = React.useState<Meta | null>(null);
  
  // Local state for concession percent input (interconnected with amount)
  const [concessionPercentInput, setConcessionPercentInput] = React.useState<string>('');
  
  // Store selected pharmacy items to preserve their uom_conversion data
  const selectedPharmacyItemsRef = React.useRef<Map<number, PharmacyItemType>>(new Map());

  // Store pharmacy items before clearing when Set All Items is checked
  const [savedPharmacyItems, setSavedPharmacyItems] = React.useState<PackageFormValues['pharmacy_items'] | null>(null);

  // State to track if selected package has is_open === true
  const [isOpenPackageSelected, setIsOpenPackageSelected] = React.useState<boolean>(false);
  const [selectedOpenPackagePatientId, setSelectedOpenPackagePatientId] = React.useState<number | null>(null);

  // Popover state for "Service From No. of Cycles" badge
  const [cyclePopoverAnchor, setCyclePopoverAnchor] = React.useState<{ element: HTMLElement | null; serviceIdx: number | null }>({
    element: null,
    serviceIdx: null,
  });

  const handleCyclePopoverOpen = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, serviceIdx: number) => {
    setCyclePopoverAnchor({ element: event.currentTarget, serviceIdx });
  };

  const handleCyclePopoverClose = () => {
    setCyclePopoverAnchor({ element: null, serviceIdx: null });
  };

  const handleCyclePopoverKeyDown = (event: React.KeyboardEvent<HTMLElement>, serviceIdx: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCyclePopoverOpen(event, serviceIdx);
    }
  };

  const { data: packageList } = useAppQuery<PackageListResponse>(
    ['packageList', selectedTariffId, patientId],
    () =>
      getRequest<PackageListResponse>(`${getPackageListURL()}?tariff_id=${selectedTariffId}&freeze=true&status=true&patient_id=${patientId}&is_valid=true`),
    {
      staleTime: 0, // No cache (always refetch)
      gcTime: 0,
      enabled: !!selectedTariffId && !!patientId, // Only fetch when tariff and patientId are available
    }
  );
  const { data: billingConfig } = useAppQuery<billingConfigResponse>(
    ['billingConfig', patientId],
    () => getRequest<billingConfigResponse>(getbillingConfigURL() + (patientId ? `?patient_id=${patientId}` : '')),
    { staleTime: 0, gcTime: 0, enabled: !!patientId, refetchOnMount: 'always' }
  );

  const { data: packageDetails, refetch: refetchPackageDetails, isFetched: isPackageDetailsFetched, isError: isPackageDetailsError } = useAppQuery<PackageFormValues>(
    ['packageDetails', storedCounselingSessionId],
    () => getRequest<PackageFormValues>(`${getPackageDetailsURL()}/${storedCounselingSessionId}/`),
    { staleTime: 0, 
      gcTime: 0,
      enabled: !!storedCounselingSessionId,
      retry: false,
    }
  );

  const { data: packageDefinationDetails, isFetching: isPackageDefinitionFetching } = useAppQuery<PackageFormValues>(
    ['packageDefinitionDetails', selectedPackageId, selectedTariffId],
    () =>
      getRequest<PackageFormValues>(`${getPackageDetails(selectedPackageId as number, patient_procedure as number)}${selectedTariffId ? `&tariff=${selectedTariffId}` : ''}`),
    {
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      gcTime: 10 * 60 * 1000,
      enabled: typeof selectedPackageId === 'number' && !isOpenPackageSelected,
    }
  );

  // Fetch open package services when is_open package is selected
  const { 
    data: openPackageServices, 
    isFetching: isOpenPackageServicesFetching,
    isError: isOpenPackageServicesError 
  } = useAppQuery<PackageFormValues>(
    ['openPackageServices', selectedOpenPackagePatientId],
    () => getRequest<PackageFormValues>(getOpenPackageServicesURL(selectedOpenPackagePatientId as number)),
    {
      staleTime: 0,
      gcTime: 0,
      enabled: isOpenPackageSelected && typeof selectedOpenPackagePatientId === 'number',
      retry: false,
    }
  );

  // Show/hide loader when package definition or open package services are being fetched
  React.useEffect(() => {
    if (isPackageDefinitionFetching || isOpenPackageServicesFetching) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isPackageDefinitionFetching, isOpenPackageServicesFetching, showLoader, hideLoader]);

  React.useEffect(() => {
    if (packageDetails) {
      setIsViewMode(true);
      setSelectedTariffId(packageDetails.package_details.tariff);
      setSelectedPackageId(packageDetails.package_details.package ?? null);
    } else if (isPackageDetailsFetched && !packageDetails) {
      // No data exists (404 or empty response), switch to edit mode
      setIsViewMode(false);
    } else if (isPackageDetailsError) {
      // Error fetching data, switch to edit mode
      setIsViewMode(false);
    }
  }, [packageDetails, isPackageDetailsFetched, isPackageDetailsError]);

  // Handle error when fetching open package services
  React.useEffect(() => {
    if (isOpenPackageServicesError && isOpenPackageSelected) {
      addAlert(tPackage('package_messages.open_package_fetch_failed', 'Failed to fetch open package details.'), 'error');
      // Reset open package state on error
      setIsOpenPackageSelected(false);
      setSelectedOpenPackagePatientId(null);
    }
  }, [isOpenPackageServicesError, isOpenPackageSelected, addAlert, tPackage]);

  // Set first billing source and tariff as default when billingConfig loads
  React.useEffect(() => {
    // Only set defaults if no existing package data and no selection yet
    if (billingConfig?.tariff_source_group?.length && !packageDetails && !selectedBillingSource) {
      const firstBillingSource = billingConfig.tariff_source_group[0];
      setSelectedBillingSource(firstBillingSource.value);
      
      // Also set first tariff from this billing source
      if (firstBillingSource.tariffs?.length) {
        const firstTariff = firstBillingSource.tariffs[0];
        setSelectedTariffId(firstTariff.tariff_id);
        setSelectedTariffName(firstTariff.tariff_name);
      }
    }
  }, [billingConfig, packageDetails, selectedBillingSource]);

  // Check if user has changed the package selection from the original saved package
  const hasPackageChanged = packageDetails
    ? selectedPackageId !== (packageDetails.package_details.package ?? null)
    : false;

  // Memoize formInitialValues to avoid expensive recalculation on every render
  const formInitialValues = React.useMemo(() => {
    // Handle open package services when is_open package is selected
    // The API can return either package_details or package_definition
    if (isOpenPackageSelected && openPackageServices) {
      const openPkg = openPackageServices;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const pkgDetails = (openPkg as any).package_definition || openPkg.package_details;
      const pkgConcessionAmount = Number(pkgDetails?.package_concession_amount ?? 0);
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mappedServiceItems: ServiceItem[] = (openPkg.service_items || []).map((item: any): ServiceItem => {
        const unitRate = Number(item.unit_rate) || 0;
        const quantity = Number(item.quantity) || 0;
        const grossAmount = unitRate * quantity;
        
        return {
          service: item.service_id ?? item.service ?? 0,
          service_name: item.service_name,
          service_source_type: item.service_source_type,
          procedure: item.procedure,
          category: '',
          gender: item.gender,
          discount_on_quantity: item.discount_on_quantity ?? item.discount_on_qty ?? false,
          unlimited_qty: item.unlimited_qty ?? item.unlimited_quantity ?? false,
          discount_percent: Number(item.discount_percent) || 0,
          discount_amount: Number(item.discount_amount) || 0,
          individual_discount_amount: Number(item.individual_discount_amount) || 0,
          individual_discount_percent: Number(item.individual_discount_percent) || 0,
          distributed_concession_amount: 0,
          quantity: quantity,
          unit_rate: unitRate,
          original_unit_rate: item.original_unit_rate ?? unitRate,
          original_quantity: item.original_quantity ?? quantity,
          unit_rate_percentage: Number(item.unit_rate_percentage ?? item.unit_percentage ?? 0),
          total_amount: Number(item.total_amount) || grossAmount,
          gross_amount: grossAmount,
          is_prescribed_from_doctor: item.is_prescribed_from_doctor,
          is_from_package: item.is_from_package,
          procedure_details: item.procedure_details || [],
        };
      });
      
      const distributedServiceItems = distributeConcessionToServices(mappedServiceItems, pkgConcessionAmount);
      return {
        ...initialValues,
        package_details: {
          ...initialValues.package_details,
          billing_source: selectedBillingSource || pkgDetails?.billing_source || initialValues.package_details.billing_source,
          tariff: selectedTariffId ?? pkgDetails?.tariff ?? initialValues.package_details.tariff,
          tariff_name: selectedTariffName || pkgDetails?.tariff_name || initialValues.package_details.tariff_name,
          package: selectedPackageId ?? pkgDetails?.package ?? 0,
          package_duration: pkgDetails?.package_duration ?? 0,
          duration_type: pkgDetails?.duration_type ?? '',
          distribution_type: pkgDetails?.distribution_type ?? null,
          package_amount: Number(pkgDetails?.package_amount ?? 0),
          service_amount: Number(pkgDetails?.service_amount ?? 0),
          pharmacy_amount: Number(pkgDetails?.pharmacy_amount ?? 0),
          service_percent: Number(pkgDetails?.service_pct ?? pkgDetails?.service_percent ?? 0),
          pharmacy_percent: Number(pkgDetails?.pharmacy_pct ?? pkgDetails?.pharmacy_percent ?? 0),
          package_concession_amount: pkgConcessionAmount,
          package_net_amount: Number(pkgDetails?.package_net_amount ?? 0),
          total_service_amount: Number(pkgDetails?.total_service_amount ?? 0),
          remark: pkgDetails?.remark || '',
          adjustable_head: pkgDetails?.adjustable_head || false,
          is_consumables: pkgDetails?.is_consumables || false,
          consumable_amount: Number(pkgDetails?.consumable_amount ?? 0),
          pharmacy_set_all_items: pkgDetails?.pharmacy_set_all_items || false,
          procedure_details: pkgDetails?.procedure_details || [],
          is_finalized: false, // Keep fields editable for open packages
        },
        service_items: distributedServiceItems,
        pharmacy_items: (() => {
          const isSetAllItems = pkgDetails?.pharmacy_set_all_items || false;
          const pharmacyItemsFromPkg = openPkg.pharmacy_items || [];
          
          if (isSetAllItems && pharmacyItemsFromPkg.length === 0) {
            return [{
              item: 0,
              item_name: '',
              quantity: 0,
              uom: null,
              discount_percent: 0,
              uom_dropdown: null,
            }];
          }
          
          return pharmacyItemsFromPkg.map((item: PharmacyItem) => ({
            item: item.item,
            item_name: item.item_name,
            quantity: Number(item.quantity) || 0,
            uom: item.uom,
            discount_percent: Number(item.discount_percent) || 0,
            total_amount: Number(item.total_amount) || 0,
            uom_dropdown: item.uom_conversion || item.uom_dropdown || null,
          }));
        })(),
      };
    }
    
    if (packageDetails && !hasPackageChanged) {
      // Get package concession for proportional distribution recalculation
      const pkgConcessionAmount = Number(packageDetails.package_details.package_concession_amount) || 0;
      
      // Map service items and recalculate proportional distribution
      const mappedServiceItems: ServiceItem[] = (packageDetails.service_items || []).map((item) => ({
        ...item,
        unlimited_qty: item.unlimited_quantity || item.unlimited_qty || false,
        individual_discount_amount: Number(item.individual_discount_amount) || 0,
        individual_discount_percent: Number(item.individual_discount_percent) || 0,
        distributed_concession_amount: 0, // Will be recalculated
        gross_amount: (Number(item.unit_rate) || 0) * (Number(item.quantity) || 0),
        original_unit_rate: item.original_unit_rate ?? item.unit_rate,
        original_quantity: item.original_quantity ?? item.quantity,
      }));
      
      // Recalculate proportional distribution of package concession
      const distributedServiceItems = distributeConcessionToServices(mappedServiceItems, pkgConcessionAmount);
      
      // Map pharmacy items to ensure uom_dropdown is populated from uom_conversion if needed
      // Also map service_pct/pharmacy_pct from API to service_percent/pharmacy_percent for form
      const isPharmacySetAllItems = packageDetails.package_details.pharmacy_set_all_items || false;
      const pharmacyItemsFromApi = packageDetails.pharmacy_items || [];
      
      // When pharmacy_set_all_items is true, ensure a blank disabled row exists
      const pharmacyItems = isPharmacySetAllItems && pharmacyItemsFromApi.length === 0
        ? [{
          item: 0,
          item_name: '',
          quantity: 0,
          uom: null,
          discount_percent: 0,
          uom_dropdown: null,
        }]
        : pharmacyItemsFromApi.map((item) => ({
          ...item,
          uom_dropdown: item.uom_conversion || item.uom_dropdown || null,
        }));

      return {
        ...packageDetails,
        package_details: {
          ...packageDetails.package_details,
          service_percent: Number(packageDetails.package_details.service_pct ?? packageDetails.package_details.service_percent ?? 0),
          pharmacy_percent: Number(packageDetails.package_details.pharmacy_pct ?? packageDetails.package_details.pharmacy_percent ?? 0),
        },
        pharmacy_items: pharmacyItems,
        service_items: distributedServiceItems,
      };
    } else if (packageDefinationDetails) {
      const def = packageDefinationDetails as unknown as PackageDefinitionFormValues;
      // Preserve billing source and tariff from packageDetails if available (when package changed)
      const preservedBillingSource = packageDetails?.package_details?.billing_source || selectedBillingSource || initialValues.package_details.billing_source;
      const preservedTariffId = packageDetails?.package_details?.tariff ?? selectedTariffId ?? initialValues.package_details.tariff;
      const preservedTariffName = packageDetails?.package_details?.tariff_name || selectedTariffName || initialValues.package_details.tariff_name;
      
      // Get package concession amount for proportional distribution
      const pkgConcessionAmount = Number(def.package_definition?.package_concession_amount ?? 0);
      
      // First map service items with individual discounts preserved
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mappedServiceItems: ServiceItem[] = (def.service_items || []).map((item: any): ServiceItem => {
        const unitRate = Number(item.unit_rate) || 0;
        const quantity = Number(item.quantity) || 0;
        const grossAmount = unitRate * quantity;
        const individualDiscountAmt = Number(item.individual_discount_amount) || 0;
        const individualDiscountPct = Number(item.individual_discount_percent) || 0;
        
        return {
          service: item.service_id ?? 0,
          service_name: item.service_name,
          service_source_type: item.service_source_type,
          procedure: item.procedure,
          category: '',
          gender: item.gender,
          discount_on_quantity: item.discount_on_quantity,
          unlimited_qty: item.unlimited_qty,
          discount_percent: 0,
          discount_amount: 0,
          individual_discount_amount: individualDiscountAmt,
          individual_discount_percent: individualDiscountPct,
          distributed_concession_amount: 0,
          quantity: quantity,
          unit_rate: unitRate,
          original_unit_rate: item.original_unit_rate ?? unitRate,
          original_quantity: item.original_quantity ?? quantity,
          unit_rate_percentage: Number(item.unit_rate_percentage ?? 0),
          total_amount: grossAmount, // Will be updated by distribution
          gross_amount: grossAmount,
          is_prescribed_from_doctor: item.is_prescribed_from_doctor,
          is_from_package: item.is_from_package,
          procedure_details: item.procedure_details || [],
        };
      });
      
      // Recalculate proportional distribution of package concession
      const distributedServiceItems = distributeConcessionToServices(mappedServiceItems, pkgConcessionAmount);
      
      return {
        ...initialValues,
        package_details: {
          ...initialValues.package_details,
          billing_source: preservedBillingSource,
          tariff: preservedTariffId,
          tariff_name: preservedTariffName,
          package:
            selectedPackageId !== null
              ? selectedPackageId
              : def.package_definition?.id || 0,
          package_duration: def.package_definition?.package_duration,
          duration_type: def.package_definition?.duration_type,
          distribution_type: def.package_definition?.distribution_type,
          package_amount: Number(def.package_definition?.package_amount ?? 0),
          service_amount: Number(def.package_definition?.service_amount ?? 0),
          pharmacy_amount: Number(def.package_definition?.pharmacy_amount ?? 0),
          service_percent: Number(def.package_definition?.service_pct ?? def.package_definition?.service_percent ?? 0),
          pharmacy_percent: Number(def.package_definition?.pharmacy_pct ?? def.package_definition?.pharmacy_percent ?? 0),
          package_concession_amount: pkgConcessionAmount,
          package_net_amount: Number(def.package_definition?.package_net_amount ?? 0),
          total_service_amount: Number(def.package_definition?.total_service_amount ?? 0),
          remark: def.package_definition?.remark || '',
          adjustable_head: def.package_definition?.adjustable_head || false,
          is_consumables: def.package_definition?.is_consumables || false,
          consumable_amount: Number(def.package_definition?.consumable_amount ?? 0),
          pharmacy_set_all_items: def.package_definition?.pharmacy_set_all_items || false,
          procedure_details: def.package_definition?.procedure_details ?? []
        },
        service_items: distributedServiceItems,
        pharmacy_items: (() => {
          const isSetAllItems = def.package_definition?.pharmacy_set_all_items || false;
          const pharmacyItemsFromDef = def.pharmacy_items || [];
          
          // When pharmacy_set_all_items is true, ensure a blank disabled row exists
          if (isSetAllItems && pharmacyItemsFromDef.length === 0) {
            return [{
              item: 0,
              item_name: '',
              quantity: 0,
              uom: null,
              discount_percent: 0,
              uom_dropdown: null,
            }];
          }
          
          return pharmacyItemsFromDef.map((item: PharmacyItem) => ({
            id: item.id,
            item: item.item,
            item_name: item.item_name,
            quantity: Number(item.quantity) || 0,
            uom: item.uom,
            discount_percent: Number(item.discount_percent) || 0,
            total_amount: 0,
            uom_dropdown: item.uom_conversion || item.uom_dropdown || null,
          }));
        })(),
      };
    } else if (selectedBillingSource || selectedTariffId) {
      // When package is cleared but billing source/tariff are selected, preserve them
      return {
        ...initialValues,
        package_details: {
          ...initialValues.package_details,
          billing_source: selectedBillingSource || initialValues.package_details.billing_source,
          tariff: selectedTariffId ?? initialValues.package_details.tariff,
          tariff_name: selectedTariffName || initialValues.package_details.tariff_name,
        },
      };
    }
    return initialValues;
  }, [packageDetails, hasPackageChanged, packageDefinationDetails, selectedBillingSource, selectedTariffId, selectedTariffName, selectedPackageId, isOpenPackageSelected, openPackageServices]);

  const pharmacyQueryParams = React.useMemo(() => [
    pharmacySearch ? `search=${encodeURIComponent(pharmacySearch)}` : '',
    selectedItemGroupCode ? `item_group=${encodeURIComponent(selectedItemGroupCode)}` : '',
    selectedItemCategoryCode ? `item_category=${encodeURIComponent(selectedItemCategoryCode)}` : '',
  ].filter(Boolean).join('&'), [
    pharmacySearch,
    selectedItemGroupCode,
    selectedItemCategoryCode,
  ]);

  const { data: pharmacyList, refetch: refetchPharmacyList } = useAppQuery<PharmacyListResponse>(
    ['pharmacyList', pharmacySearch, selectedItemGroupCode, selectedItemCategoryCode],
    () => getRequest<PharmacyListResponse>(
      getPharmacyListURL() + (pharmacyQueryParams ? `&${pharmacyQueryParams}` : '')
    ),
    { 
      staleTime: 5 * 60 * 1000,
      debounceMs: 300
    }
  );

  

  const shouldFetchServices = !!serviceSearch;
  const { data: servicesList } = useAppQuery<ServiceMasterItem[]>(
    ['packageServicesList', serviceSearch],
    () => getRequest<ServiceMasterItem[]>(
      getServicesURL() +
      (serviceSearch
        ? `?search_query=${encodeURIComponent(serviceSearch)}&tariff_id=${selectedTariffId}`
        : '')
    ),
    {
      staleTime: 5 * 60 * 1000,
      enabled: shouldFetchServices
    }
  );

  
  const {
    isError: postIsError,
    error: postError,
    isSuccess: postIsSuccess,
    isFetching: isSavingPackage,
  } = useAppQuery(
    ['savePackageDetails', submitPayload, packageDetails],
    () => {
      if (!submitPayload) return Promise.resolve(null);

      // If packageDetails exist → use PUT
      const isUpdate = !!packageDetails;

      if (!isUpdate) {
      // CREATE
        return postRequest(PackageDetailsURL(), submitPayload);
      }

      // UPDATE
      return putRequest(
        `${PackageDetailsURL()}${packageDetails.package_details.id}/`,
        submitPayload
      );
    },
    {
      enabled: !!submitPayload,
      staleTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );



  React.useEffect(() => {
    // Only handle once per submissionId
    if (submitPayload && lastHandledIdRef.current !== submissionId) {
      if (postIsSuccess) {
        addAlert(tPackage('package_messages.package_save_success', 'Package details saved successfully.'), 'success');
        lastHandledIdRef.current = submissionId;
        setSubmitPayload(null); // reset so query won't rerun
        refetchPackageDetails(); // refetch package details after save
        // Navigate back to the package listing
        // navigate('/admin/billing/package', { state: { refetch: true, ts: Date.now() } });
      } else if (postIsError) {
        addAlert(tPackage('package_messages.package_save_failed', 'Failed to save package details.'), 'error');
        // eslint-disable-next-line no-console
        console.error('Post package details error:', postError);
        refetchPackageDetails(); // refetch package details after save
        lastHandledIdRef.current = submissionId;
        setSubmitPayload(null);
      }
    }
  }, [postIsSuccess, postIsError, postError, submitPayload, submissionId, addAlert, navigate, tPackage, refetchPackageDetails]);


  function buildPayload(values: PackageFormValues) {
    // Calculate total service concession (only manually added extra concessions, NOT distributed package concession)
    const totalServiceConcessionAmount = (values.service_items || []).reduce((sum, si) => {
      const individualAmt = Number(si.individual_discount_amount) || 0;
      const qty = Number(si.quantity) || 0;
      return sum + (individualAmt * qty);
    }, 0);
    const totalPackageAmount = Number(values.package_details.package_amount) || 0;
    const totalServiceConcessionPercent = totalPackageAmount > 0
      ? Number(((totalServiceConcessionAmount / totalPackageAmount) * 100).toFixed(2))
      : 0;

    // Base payload
    const basePayload = {
      package_details: {
        ...packageDetails ? { id: packageDetails.package_details.id } : {},
        counseling_session: storedCounselingSessionId,
        patient: patientId,
        package: values.package_details.package,
        billing_source: values.package_details.billing_source,
        tariff: values.package_details.tariff,
        tariff_name: values.package_details.tariff_name,
        duration_type: values.package_details.duration_type,
        distribution_type: values.package_details.distribution_type,
        package_duration: values.package_details.package_duration,
        pharmacy_amount: values.package_details.pharmacy_amount,
        package_amount: values.package_details.package_amount,
        service_amount: values.package_details.service_amount,
        service_pct: values.package_details.service_percent,
        pharmacy_pct: values.package_details.pharmacy_percent,
        package_net_amount: values.package_details.package_net_amount,
        remark: values.package_details.remark,
        is_finalized: values.package_details.is_finalized,
        package_concession_amount: values.package_details.package_concession_amount,
        total_service_concession_amount: Number(totalServiceConcessionAmount.toFixed(2)),
        total_service_concession_percent: totalServiceConcessionPercent,
        adjustable_head: values.package_details.adjustable_head || false,
        is_consumables: values.package_details.is_consumables || false,
        consumable_amount: values.package_details.consumable_amount || 0,
        pharmacy_set_all_items: values.package_details.pharmacy_set_all_items || false,
        // Add secondary package fields when is_open package is selected
        ...(isOpenPackageSelected && selectedOpenPackagePatientId ? {
          is_secondary_package: true,
          primary_package: selectedOpenPackagePatientId,
        } : {}),
        procedure_details: (values.package_details.procedure_details || []).map(proc => ({
          ...(packageDetails && proc.id ? { id: proc.id } : {}),
          procedure_id: proc.procedure_id,
          procedure_source_type: proc.procedure_source_type,
          procedure_source_name: proc.procedure_source_name,
        })),
      },
      service_items: values.service_items.map(item => ({
        ...packageDetails ? { id: item.id } : {},
        service: item.service,
        service_name: item.service_name,
        service_source_type: item.service_source_type,
        procedure: item.procedure,
        category: item.category,
        gender: item.gender,
        discount_percent: item.discount_percent,
        discount_amount: item.discount_amount,
        individual_discount_amount: item.individual_discount_amount,
        individual_discount_percent: item.individual_discount_percent,
        distributed_concession_amount: item.distributed_concession_amount,
        discount_on_quantity: item.discount_on_quantity,
        quantity: item.quantity,
        unit_rate: item.unit_rate,
        is_prescribed_from_doctor: item.is_prescribed_from_doctor,
        unlimited_quantity: item.unlimited_qty,
        unit_rate_percentage: item.unit_rate_percentage,
        total_amount: item.total_amount,
        gross_amount: item.gross_amount,
        is_from_package: item.is_from_package,
        procedure_details: item.procedure_details || [],
      })),
      pharmacy_items: values.package_details.pharmacy_set_all_items
        ? []
        : values.pharmacy_items.map(item => ({
          ...packageDetails ? { id: item.id } : {},
          item: item.item,
          item_name: item.item_name,
          uom: item.uom,
          quantity: item.quantity,
          discount_percent: item.discount_percent,
          total_amount: item.total_amount,
        })),
    };

    return basePayload;
  }

  const handleSubmit = (values: PackageFormValues) => {
    const payload = buildPayload(values);
    setSubmitPayload(payload);
  };

  // Confirmation modal state
  const [openModal, setOpenModal] = React.useState(false);

  // Delete confirmation modal state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [pendingDeleteAction, setPendingDeleteAction] = React.useState<(() => void) | null>(null);

  const handleDeleteClick = (deleteAction: () => void) => {
    setPendingDeleteAction(() => deleteAction);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (pendingDeleteAction) {
      pendingDeleteAction();
    }
    setDeleteConfirmOpen(false);
    setPendingDeleteAction(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setPendingDeleteAction(null);
  };

  // Wrap Formik submit to show confirmaton modal
  const handleOpenModal = () => {
    setOpenModal(true);
  };

  // Actual submit after confirmation
  const handleConfirmedSubmit = (values: PackageFormValues) => {
    if (values) {
      handleSubmit(values);
      setOpenModal(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };
  // Keep MetaForStatus in sync with server-provided meta without causing render loops
  React.useEffect(() => {
    if (packageDetails?.meta) {
      setMetaForStatus(packageDetails.meta);
    } else if (packageDefinationDetails?.meta) {
      setMetaForStatus(packageDefinationDetails.meta);
    } else {
      setMetaForStatus(null);
    }
  }, [packageDetails, packageDefinationDetails]);
  // Use isViewMode state to control view mode
  if (isViewMode) {
    if (!packageDetails) return null;
    return (
      <PackageDetailsViewMode
        data={packageDetails}
        onBack={() => setIsViewMode(false)}
      />
    );
  }
 

  return (
    <>
      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleOpenModal}
      >
        {({ values, setFieldValue, handleChange, handleBlur, errors, touched }) => {
          const packageAmount = values.package_details?.package_amount || 0;
          const pkgAmt = Number(packageAmount) || 0;
          const pharmAmt = Number(values.package_details.pharmacy_amount) || 0;
          const servAmt = Number(values.package_details.service_amount) || 0;
          const pharmPercent = pkgAmt ? ((pharmAmt / pkgAmt) * 100).toFixed(2) : '0.00';
          const servPercent = pkgAmt ? ((servAmt / pkgAmt) * 100).toFixed(2) : '0.00';
          
          // Package-level concession (distributed to all services)
          const distributedPackageConcession = Number(values.package_details.package_concession_amount) || 0;
          
          // Extra service-level concession (sum of individual discounts across all services)
          const totalExtraServiceConcession = (values.service_items || []).reduce((sum, si) => {
            const individualAmt = Number(si.individual_discount_amount) || 0;
            const qty = Number(si.quantity) || 0;
            return sum + (individualAmt * qty);
          }, 0);
          
          // Total concession display = distributed + extra service-level
          const totalConcessionDisplay = Number((distributedPackageConcession + totalExtraServiceConcession).toFixed(2));
          
          // Net amount is synced via FormValuesSync component
          const netAmount = pkgAmt - totalConcessionDisplay;

          // --- Custom validation summary flags ---
          // Total Services Amount = sum of gross amounts (rate * qty) across all services
          const totalServicesAmount = (values.service_items || []).reduce((sum, si) => {
            const rowGross = (Number(si.unit_rate) || 0) * (Number(si.quantity) || 0);
            return sum + rowGross;
          }, 0);

          const serviceAmountNumeric = Number(values.package_details.service_amount || 0);
          const isServiceAmountMatch =
            Number(serviceAmountNumeric.toFixed(2)) === Number(totalServicesAmount.toFixed(2));

          const hasAtLeastOneService = (values.service_items || []).some(
            si => si.service || si.service_name
          );

          const pharmacySetAllItems = values.package_details?.pharmacy_set_all_items || false;
          const hasAtLeastOnePharmacy = pharmacySetAllItems || (values.pharmacy_items || []).some(
            pi => pi.item || pi.item_name
          );

          const hasMinRows = hasAtLeastOneService && hasAtLeastOnePharmacy;

          // --- Concession validation based on CONCESSION rule from selected package ---
          const selectedPackage = (Array.isArray(packageList?.objects) ? packageList.objects : []).find(
            (pkg) => pkg.id === values.package_details.package
          );
          const concessionRule = selectedPackage?.rules?.find(
            (rule) => rule.rule_code === 'CONCESSION' && rule.active
          );
          const currentConcessionPercent = (() => {
            const concessionAmt = Number(values.package_details.package_concession_amount) || 0;
            const srvAmount = Number(values.package_details.service_amount) || 0;
            return srvAmount > 0 ? (concessionAmt / srvAmount) * 100 : 0;
          })();
          const concessionMinPercent = concessionRule ? Number(concessionRule.min_percent) || 0 : 0;
          const concessionMaxPercent = concessionRule ? Number(concessionRule.max_percent) || 100 : 100;
          const isConcessionBelowMin = concessionRule && currentConcessionPercent > 0 && currentConcessionPercent < concessionMinPercent;
          const isConcessionAboveMax = concessionRule && currentConcessionPercent > concessionMaxPercent;
          const isConcessionOutOfRange = isConcessionBelowMin || isConcessionAboveMax;
          const concessionValidationMessage = isConcessionOutOfRange
            ? tBilling('package_details.concession_validation_error', {
              min: concessionMinPercent.toFixed(2),
              max: concessionMaxPercent.toFixed(2),
            }) || `Concession must be between ${concessionMinPercent.toFixed(2)}% and ${concessionMaxPercent.toFixed(2)}%`
            : '';

          // --- Rate Edit validation based on RATE_EDIT rule from selected package ---
          const rateEditRule = selectedPackage?.rules?.find(
            (rule) => rule.rule_code === 'RATE_EDIT' && rule.active
          );
          const isPackageAmountEditable = Boolean(rateEditRule);
          const rateEditMinAmount = rateEditRule?.min_amount ? Number(rateEditRule.min_amount) : null;
          const rateEditMaxAmount = rateEditRule?.max_amount ? Number(rateEditRule.max_amount) : null;
          const currentPackageAmount = Number(values.package_details.package_amount) || 0;

          // Validate package amount against RATE_EDIT rule min/max
          let packageAmountError = '';
          if (isPackageAmountEditable && currentPackageAmount > 0) {
            if (rateEditMinAmount !== null && currentPackageAmount < rateEditMinAmount) {
              packageAmountError = tBilling('package_details.rate_validation_error', {
                min: rateEditMinAmount,
                max: rateEditMaxAmount ?? '∞',
              }) || `Amount must be between ${rateEditMinAmount} and ${rateEditMaxAmount ?? '∞'}`;
            } else if (rateEditMaxAmount !== null && currentPackageAmount > rateEditMaxAmount) {
              packageAmountError = tBilling('package_details.rate_validation_error', {
                min: rateEditMinAmount ?? 0,
                max: rateEditMaxAmount,
              }) || `Amount must be between ${rateEditMinAmount ?? 0} and ${rateEditMaxAmount}`;
            }
          }
          const hasPackageAmountError = packageAmountError !== '';

          const allCustomValid = isServiceAmountMatch && hasMinRows && !isConcessionOutOfRange && !hasPackageAmountError;

          const isAmount = values.package_details.distribution_type === 'amount';
          // For percent mode, use percent fields for input, calculate amount for display
          const pharmacyDisplay = isAmount
            ? values.package_details.pharmacy_amount
            : values.package_details.pharmacy_percent;
          const serviceDisplay = isAmount
            ? values.package_details.service_amount
            : values.package_details.service_percent;


          const selectedBillingSourceGroup =
          (billingConfig?.tariff_source_group || []).find(grp => grp.value === values.package_details.billing_source) || null;

          const availableTariffs = selectedBillingSourceGroup?.tariffs || [];

          return (
            <Box data-testid="create-package-container" sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{tBilling('package_details.package_details')}</Typography>
              <Form data-testid="create-package-form" autoComplete="off">
                <FormValuesSync values={values} setFieldValue={setFieldValue} />
                <Box>
                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xl: 3, lg: 4, md: 6, xs: 12 }}>
                      <Autocomplete
                        data-testid="package-billing-source-select"
                        options={billingConfig?.tariff_source_group || []}
                        getOptionLabel={option => option.label}
                        disabled={values.package_details.is_finalized}
                        sx={values.package_details.is_finalized ? {
                          backgroundColor: '#fafafa',
                          opacity: 0.9,
                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                        } : undefined}
                        value={
                          (billingConfig?.tariff_source_group || []).find(
                            opt => opt.value === values.package_details.billing_source
                          ) || null
                        }
                        onChange={(_, newValue) => {
                          setFieldValue('package_details.billing_source', newValue ? newValue.value : '');
                          setSelectedBillingSource(newValue ? newValue.value : '');
                          // reset tariff when billing source changes
                          setFieldValue('package_details.tariff', null);
                          setFieldValue('package_details.tariff_name', '');
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={RequiredLabel(tBilling('package_details.billing_source'), true)}
                            size="small"
                            error={
                              touched.package_details?.billing_source &&
          Boolean(errors.package_details?.billing_source)
                            }
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                      />
                    </Grid2>
                    <Grid2 size={{ xl: 3, lg: 4, md: 6, xs: 12 }}>
                      <Autocomplete
                        data-testid="package-tariff-select"
                        options={availableTariffs}
                        getOptionLabel={option => option.tariff_name}
                        disabled={
                          values.package_details.is_finalized ||
                          !values.package_details.billing_source
                        }
                        sx={values.package_details.is_finalized ? {
                          backgroundColor: '#fafafa',
                          opacity: 0.9,
                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                        } : undefined}
                        value={
                          availableTariffs.find(
                            opt => opt.tariff_id === values.package_details.tariff
                          ) || null
                        }
                        onChange={(_, newValue) => {
                          const newTariffId = newValue ? newValue.tariff_id : null;
                          // Clear package-dependent data first when tariff changes/clears
                          setFieldValue('service_items', []);
                          setFieldValue('pharmacy_items', []);
                          selectedPharmacyItemsRef.current.clear();
                          setSavedPharmacyItems(null);
                          setSelectedDrug(null);
                          setInputValue('');
                          setSelectedPackageId(null);
                          
                          // Reset is_open package state when tariff changes
                          setIsOpenPackageSelected(false);
                          setSelectedOpenPackagePatientId(null);

                          // Reset package details tied to selected tariff/package
                          setFieldValue('package_details', {
                            ...values.package_details,
                            tariff: newTariffId,
                            tariff_name: newValue ? newValue.tariff_name : '',
                            package: null,
                            package_amount: 0,
                            service_amount: 0,
                            pharmacy_amount: 0,
                            service_percent: 0,
                            pharmacy_percent: 0,
                            package_concession_amount: 0,
                            total_service_amount: 0,
                            package_net_amount: 0,
                            pharmacy_set_all_items: false,
                          });
                          setSelectedTariffId(newTariffId);
                          setSelectedTariffName(newValue ? newValue.tariff_name : '');
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={RequiredLabel(tBilling('package_details.tariff'), true)}
                            size="small"
                            error={
                              touched.package_details?.tariff &&
                              Boolean(errors.package_details?.tariff)
                            }
                          />
                        )}
                        isOptionEqualToValue={(option, value) =>
                          option.tariff_id === value.tariff_id
                        }
                      />
                    </Grid2>
                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      <Autocomplete
                        data-testid="package-name-select"
                        options={Array.isArray(packageList?.objects) ? packageList.objects : []}
                        getOptionLabel={option => option.package_name}
                        disabled={values.package_details.is_finalized}
                        sx={values.package_details.is_finalized ? {
                          backgroundColor: '#fafafa',
                          opacity: 0.9,
                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                        } : undefined}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <span>{option.package_name}</span>
                            {option.is_open && (
                              <Chip
                                label={tBilling('package_details.existing', { defaultValue: 'Existing' })}
                                size="small"
                                sx={{ ml: 1, color: '#16a34a', border: '1px solid #16a34a', backgroundColor: 'transparent' }}
                              />
                            )}
                          </Box>
                        )}
                        value={(Array.isArray(packageList?.objects) ? packageList.objects : []).find(opt => opt.id === values.package_details.package) || null}
                        onChange={(_, newValue) => {
                          const newPackageId = newValue ? newValue.id : null;
                          const isOpenPackage = newValue?.is_open === true;
                          const openPackagePatientId = newValue?.patient_package_id ?? null;
                          
                          // Update is_open package state
                          setIsOpenPackageSelected(isOpenPackage);
                          setSelectedOpenPackagePatientId(openPackagePatientId);
                          
                          // Use startTransition for heavy updates to keep UI responsive
                          React.startTransition(() => {
                            // Clear current rows first so stale data is removed before new package data patches in
                            setFieldValue('service_items', []);
                            setFieldValue('pharmacy_items', []);
                            selectedPharmacyItemsRef.current.clear();
                            setSavedPharmacyItems(null);
                            setSelectedDrug(null);
                            setInputValue('');

                            // Reset package-level values tied to selected package
                            setFieldValue('package_details', {
                              ...values.package_details,
                              package: newPackageId,
                              package_amount: newValue ? newValue.package_amount : 0,
                              service_amount: 0,
                              pharmacy_amount: 0,
                              service_percent: 0,
                              pharmacy_percent: 0,
                              package_concession_amount: 0,
                              total_service_amount: 0,
                              package_net_amount: 0,
                              pharmacy_set_all_items: false,
                            });
                            setSelectedPackageId(newPackageId);
                          });
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={RequiredLabel(tBilling('package_details.package_name'), true)}

                            size="small"
                            error={touched.package_details?.package && Boolean(errors.package_details?.package)}
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                      />
                    </Grid2>
                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      {(() => {
                        const isFieldDisabled = values.package_details.is_finalized || !isPackageAmountEditable || isOpenPackageSelected;
                        return (
                          <TextField
                            data-testid="package-amount-input"
                            fullWidth
                            size="small"
                            type="number"
                            label={RequiredLabel(tBilling('package_details.package_amount'), true)}
                            disabled={isFieldDisabled}
                            sx={isFieldDisabled ? {
                              backgroundColor: '#fafafa',
                              opacity: 0.9,
                              transition: 'background-color 0.3s ease, opacity 0.3s ease',
                            } : undefined}
                            name="package_details.package_amount"
                            value={values.package_details.package_amount}
                            onChange={(e) => {
                              if (isFieldDisabled) return;
                              const newAmount = Number(e.target.value) || 0;
                              // Batch all updates in a single setFieldValue call
                              setFieldValue('package_details', {
                                ...values.package_details,
                                package_amount: newAmount,
                                service_amount: 0,
                                pharmacy_amount: 0,
                                service_percent: 0,
                                pharmacy_percent: 0,
                              });
                            }}
                            slotProps={{
                              input: isFieldDisabled ? { readOnly: true } : undefined,
                              htmlInput: { min: 0 }
                            }}
                            error={Boolean(packageAmountError)}
                            helperText={packageAmountError}
                          />
                        );
                      })()}
                    </Grid2>
                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      <TextField
                        data-testid="package-duration-input"
                        fullWidth
                        size="small"
                        label={RequiredLabel(tBilling('package_details.package_duration'), true)}

                        disabled={values.package_details.is_finalized || isOpenPackageSelected}
                        sx={(values.package_details.is_finalized || isOpenPackageSelected) ? {
                          backgroundColor: '#fafafa',
                          opacity: 0.9,
                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                        } : undefined}
                        name="package_details.package_duration"
                        value={values.package_details.package_duration}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.package_details?.package_duration && Boolean(errors.package_details?.package_duration)}
                      />
                    </Grid2>
                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      <Autocomplete
                        data-testid="package-duration-type-select"
                        options={billingConfig?.DURATION_TYPE || []}
                        getOptionLabel={option => String(option.name)}
                        disabled={values.package_details.is_finalized || isOpenPackageSelected}
                        sx={(values.package_details.is_finalized || isOpenPackageSelected) ? {
                          backgroundColor: '#fafafa',
                          opacity: 0.9,
                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                        } : undefined}
                        value={billingConfig?.DURATION_TYPE.find(opt => opt.code === values.package_details.duration_type) || null}
                        onChange={(_, newValue) => setFieldValue('package_details.duration_type', newValue ? newValue.code : '')}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={RequiredLabel(tBilling('package_details.duration_type'), true)}
                            size="small"
                            error={touched.package_details?.duration_type && Boolean(errors.package_details?.duration_type)}
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                      />
                    </Grid2>

                  </Grid2>
                  <Grid2 container spacing={2} sx={{ mt: 2 }}>
                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Typography sx={{ fontWeight: 500, mr: 2, whiteSpace: 'nowrap' }}>{tBilling('package_details.distribution_details')}</Typography>
                        <RadioGroup
                          data-testid="package-distribution-type-radio"
                          row
                          name="package_details.distribution_type"
                          value={values.package_details.distribution_type}
                          onChange={(e) => {
                            const newDistributionType = e.target.value;
                            handleChange(e);
                            // Recalculate values when switching distribution types only if target fields are empty
                            if (newDistributionType === 'amount' && pkgAmt > 0) {
                              // Switching to amount mode: only recalculate if amounts are 0 and percentages have values
                              const currentServicePercent = Number(values.package_details.service_percent) || 0;
                              const currentPharmacyPercent = Number(values.package_details.pharmacy_percent) || 0;
                              if (servAmt === 0 && currentServicePercent > 0) {
                                const newServiceAmt = (pkgAmt * currentServicePercent) / 100;
                                setFieldValue('package_details.service_amount', Number(newServiceAmt.toFixed(2)));
                              }
                              if (pharmAmt === 0 && currentPharmacyPercent > 0) {
                                const newPharmacyAmt = (pkgAmt * currentPharmacyPercent) / 100;
                                setFieldValue('package_details.pharmacy_amount', Number(newPharmacyAmt.toFixed(2)));
                              }
                            } else if (newDistributionType === 'percentage' && pkgAmt > 0) {
                              // Switching to percentage mode: only recalculate if percentages are 0 and amounts have values
                              const currentServicePercent = Number(values.package_details.service_percent) || 0;
                              const currentPharmacyPercent = Number(values.package_details.pharmacy_percent) || 0;
                              if (currentServicePercent === 0 && servAmt > 0) {
                                const newServicePercent = (servAmt / pkgAmt) * 100;
                                setFieldValue('package_details.service_percent', Number(newServicePercent.toFixed(2)));
                              }
                              if (currentPharmacyPercent === 0 && pharmAmt > 0) {
                                const newPharmacyPercent = (pharmAmt / pkgAmt) * 100;
                                setFieldValue('package_details.pharmacy_percent', Number(newPharmacyPercent.toFixed(2)));
                              }
                            }
                          }}

                          sx={{ ml: 1 }}
                        >
                          <FormControlLabel data-testid="package-distribution-amount-radio" value="amount"  disabled={values.package_details.is_finalized || isOpenPackageSelected}
                            sx={(values.package_details.is_finalized || isOpenPackageSelected) ? {
                              backgroundColor: '#fafafa',
                              opacity: 0.9,
                              transition: 'background-color 0.3s ease, opacity 0.3s ease',
                            } : undefined} control={<Radio color="error" />} label={tBilling('package_details.amount')} />
                          <FormControlLabel data-testid="package-distribution-percent-radio" value="percentage" disabled={values.package_details.is_finalized || isOpenPackageSelected}
                            sx={(values.package_details.is_finalized || isOpenPackageSelected) ? {
                              backgroundColor: '#fafafa',
                              opacity: 0.9,
                              transition: 'background-color 0.3s ease, opacity 0.3s ease',
                            } : undefined} control={<Radio color="error" />} label={tBilling('package_details.percentage')} />
                        </RadioGroup>
                      </Box>
                    </Grid2>
                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      <TextField
                        data-testid="package-service-distribution-input"
                        fullWidth
                        size="small"
                        disabled={pkgAmt === 0 || !values.package_details.distribution_type  || values.package_details.is_finalized || isOpenPackageSelected}
                        sx={(values.package_details.is_finalized || isOpenPackageSelected) ? {
                          backgroundColor: '#fafafa',
                          opacity: 0.9,
                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                        } : undefined}
                        label={
                          isAmount
                            ? `${tBilling('package_details.service_amount')} (${servPercent}%)`
                            : `${tBilling('package_details.service_percentage')} (${servAmt})`
                        }
                        name={isAmount ? 'package_details.service_amount' : 'package_details.service_percent'}
                        value={serviceDisplay}
                        slotProps={{
                          htmlInput: {
                            min: 0,
                            max: isAmount ? pkgAmt : 100.00,
                            step: 'any',
                          },
                          input: isAmount ? undefined : { endAdornment: <span>%</span> }
                        }}
                        onChange={e => {
                          if (isAmount) {
                            const rounded = Number((Number(e.target.value) || 0).toFixed(2));
                            const newService = Math.max(0, Math.min(rounded, pkgAmt || 0));
                            const complement = pkgAmt ? Math.max(0, Number((pkgAmt - newService).toFixed(2))) : 0;
                            setFieldValue('package_details.service_amount', newService);
                            setFieldValue('package_details.pharmacy_amount', complement);
                            setFieldValue('package_details.service_percent', pkgAmt ? Number(((newService / pkgAmt) * 100).toFixed(2)) : 0);
                            setFieldValue('package_details.pharmacy_percent', pkgAmt ? Number(((complement / pkgAmt) * 100).toFixed(2)) : 0);
                          } else {
                            const rounded = Number((Number(e.target.value) || 0).toFixed(2));
                            const percent = Math.max(0, Math.min(rounded, 100));
                            const complementPercent = Math.max(0, Number((100 - percent).toFixed(2)));
                            const rawAmt = pkgAmt ? (pkgAmt * percent) / 100 : 0;
                            const newService = Math.max(0, Math.min(Number(rawAmt.toFixed(2)), pkgAmt || 0));
                            const complement = pkgAmt ? Math.max(0, Number((pkgAmt - newService).toFixed(2))) : 0;
                            setFieldValue('package_details.service_percent', percent);
                            setFieldValue('package_details.pharmacy_percent', complementPercent);
                            setFieldValue('package_details.service_amount', newService);
                            setFieldValue('package_details.pharmacy_amount', complement);
                          }
                        }}
                        onBlur={handleBlur}
                        error={(touched.package_details?.service_amount && Boolean(errors.package_details?.service_amount)) || (touched.package_details?.service_amount && typeof errors.package_details === 'string')}
                  
                      />
                    </Grid2>

                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      <TextField
                        data-testid="package-pharmacy-distribution-input"
                        fullWidth

                        disabled={pkgAmt === 0 || !values.package_details.distribution_type || values.package_details.is_finalized || isOpenPackageSelected}
                        sx={(values.package_details.is_finalized || isOpenPackageSelected) ? {
                          backgroundColor: '#fafafa',
                          opacity: 0.9,
                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                        } : undefined}
                        label={
                          isAmount
                            ? `${tBilling('package_details.pharmacy_amount')} (${pharmPercent}%)`
                            : `${tBilling('package_details.pharmacy_percentage')} (${pharmAmt})`
                        }
                        name={isAmount ? 'package_details.pharmacy_amount' : 'package_details.pharmacy_percent'}
                        color="error"
                        size="small"
                        value={pharmacyDisplay}
                        slotProps={{
                          htmlInput: {
                            min: 0,
                            max: isAmount ? pkgAmt : 100.00,
                            step: 'any',
                          },
                          input: isAmount ? undefined : { endAdornment: <span>%</span> }
                        }}
                        onChange={e => {
                          if (isAmount) {
                            const rounded = Number((Number(e.target.value) || 0).toFixed(2));
                            const newPharmacy = Math.max(0, Math.min(rounded, pkgAmt || 0));
                            const complement = pkgAmt ? Math.max(0, Number((pkgAmt - newPharmacy).toFixed(2))) : 0;
                            setFieldValue('package_details.pharmacy_amount', newPharmacy);
                            setFieldValue('package_details.service_amount', complement);
                            setFieldValue('package_details.pharmacy_percent', pkgAmt ? Number(((newPharmacy / pkgAmt) * 100).toFixed(2)) : 0);
                            setFieldValue('package_details.service_percent', pkgAmt ? Number(((complement / pkgAmt) * 100).toFixed(2)) : 0);
                          } else {
                            const rounded = Number((Number(e.target.value) || 0).toFixed(2));
                            const percent = Math.max(0, Math.min(rounded, 100));
                            const complementPercent = Math.max(0, Number((100 - percent).toFixed(2)));
                            const rawAmt = pkgAmt ? (pkgAmt * percent) / 100 : 0;
                            const newPharmacy = Math.max(0, Math.min(Number(rawAmt.toFixed(2)), pkgAmt || 0));
                            const complement = pkgAmt ? Math.max(0, Number((pkgAmt - newPharmacy).toFixed(2))) : 0;
                            setFieldValue('package_details.pharmacy_percent', percent);
                            setFieldValue('package_details.service_percent', complementPercent);
                            setFieldValue('package_details.pharmacy_amount', newPharmacy);
                            setFieldValue('package_details.service_amount', complement);
                          }
                        }}
                        onBlur={handleBlur}
                        error={(touched.package_details?.pharmacy_amount && Boolean(errors.package_details?.pharmacy_amount)) || (touched.package_details?.pharmacy_amount && typeof errors.package_details === 'string')}
                  
                      />
                    </Grid2>
                  </Grid2>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 5, gap: 2 }}>
                  <Button
                    data-testid="package-services-tab-button"
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      fontWeight: 600,
                      fontSize: 16,
                      color: activeTab === 'services' ? '#e57353' : '#bdbdbd',
                      borderColor: activeTab === 'services' ? '#e57353' : '#bdbdbd',
                      backgroundColor: '#fff',
                    }}
                    onClick={() => setActiveTab('services')}
                  >
                    {tBilling('package_details.services')}
                  </Button>
                  <Button
                    data-testid="package-pharmacy-tab-button"
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      fontWeight: 600,
                      fontSize: 16,
                      color: activeTab === 'pharmacy' ? '#e57353' : '#bdbdbd',
                      borderColor: activeTab === 'pharmacy' ? '#e57353' : '#bdbdbd',
                      backgroundColor: '#fff',
                    }}
                    onClick={() => setActiveTab('pharmacy')}
                  >
                    {tBilling('package_details.pharmacy_items')}
                  </Button>
                </Box>


                {/* Services Section */}

                {activeTab === 'services' && (
                  <Box >
                    <FieldArray name="service_items">

                      {() => 
                      {
                        const PackageServicesList = Array.isArray(servicesList) ? servicesList: [];

                        return(
                          <>
                            <Grid2 container spacing={2} alignItems="center" justifyContent="space-between">
                              {/* Procedure Cycles Chips */}
                              <Grid2 size="auto">
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', marginTop: 0.5, gap: 1, alignItems: 'center' }}>
                                  {(values.package_details.procedure_details as ProcedureDetailItem[] | undefined)?.map((proc) => (
                                    <Chip
                                      key={proc.id ?? `${proc.procedure_id}-${proc.procedure_source_type}`}
                                      label={proc.procedure_source_name}
                                      size="small"
                                      sx={{
                                        backgroundColor: '#FFF4F2',
                                        color: '#000000',
                                        fontWeight: 500,
                                        px: 1,
                                        py: 0.5,
                                        fontSize: 14,
                                        minWidth: 100,
                                        borderRadius: '16px',
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Grid2>
                              <Grid2 size={{xl:4, lg:6, md:4, xs:12}}>
                                <Autocomplete
                                  data-testid="package-search-service-select"
                                  disablePortal
                                  fullWidth
                                  size="small"
                                  disabled={values.package_details.is_finalized || isOpenPackageSelected}
                                  sx={(values.package_details.is_finalized || isOpenPackageSelected) ? {
                                    backgroundColor: '#fafafa',
                                    opacity: 0.9,
                                    transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                  } : undefined}
                                  options={inputValueForService.trim() ? PackageServicesList:[]}
                                  open={PackageServicesList.length > 0 && inputValueForService.trim().length > 0}
                                  value={selectedService}
                                  inputValue={inputValueForService}
                                  onChange={(e, value) => {
                                    setSelectedService(value);
                                    if (value) {
                                      const v = value as ServiceMasterItem;
                                      const pkgConcession = Number(values.package_details.package_concession_amount) || 0;
                                      
                                      if (
                                        values.service_items &&
                                      values.service_items.length > 0 &&
                                      !values.service_items[0].service_name
                                      ) {
                                        // Replacing the first empty service
                                        const newServiceItem: ServiceItem = {
                                          service: v.id,
                                          service_name: v.name,
                                          service_source_type: v.service_type,
                                          procedure: '',
                                          category: '',
                                          gender: 'both',
                                          discount_percent: 0,
                                          discount_amount: 0,
                                          individual_discount_amount: 0,
                                          individual_discount_percent: 0,
                                          distributed_concession_amount: 0,
                                          discount_on_quantity: false,
                                          unlimited_qty: false,
                                          quantity: 1,
                                          unit_rate: Number(v.charges || 0),
                                          original_unit_rate: Number(v.charges || 0),
                                          original_quantity: 1,
                                          unit_rate_percentage: 0,
                                          gross_amount: v.charges || 0,
                                          total_amount: 0,
                                          is_from_package: false,
                                          is_prescribed_from_doctor: false,
                                          procedure_details: []
                                        };
                                        const updatedServices = [newServiceItem, ...values.service_items.slice(1)];
                                        const redistributed = distributeConcessionToServices(updatedServices, pkgConcession);
                                        setFieldValue('service_items', redistributed);
                                        
                                        // Update total_service_amount
                                        const newTotalServiceAmount = redistributed.reduce((sum, s) => {
                                          const gross = (Number(s.unit_rate) || 0) * (Number(s.quantity) || 0);
                                          return sum + gross;
                                        }, 0);
                                        setFieldValue('package_details.total_service_amount', Number(newTotalServiceAmount.toFixed(2)));
                                      } else {
                                        // Adding a new service
                                        const newServiceItem: ServiceItem = {
                                          service: v.id,
                                          service_name: v.name,
                                          service_source_type: v.service_type,
                                          procedure: '',
                                          category: '',
                                          gender: 'both',
                                          discount_percent: 0,
                                          discount_amount: 0,
                                          individual_discount_amount: 0,
                                          individual_discount_percent: 0,
                                          distributed_concession_amount: 0,
                                          discount_on_quantity: false,
                                          unlimited_qty: false,
                                          quantity: 1,
                                          unit_rate: v.charges || 0,
                                          original_unit_rate: v.charges || 0,
                                          original_quantity: 1,
                                          unit_rate_percentage: 0,
                                          gross_amount: v.charges || 0,
                                          total_amount: 0,
                                          is_from_package: false,
                                          is_prescribed_from_doctor: false,
                                          procedure_details: []
                                        };
                                        const updatedServices = [...(values.service_items || []), newServiceItem];
                                        const redistributed = distributeConcessionToServices(updatedServices, pkgConcession);
                                        setFieldValue('service_items', redistributed);
                                        
                                        // Update total_service_amount
                                        const newTotalServiceAmount = redistributed.reduce((sum, s) => {
                                          const gross = (Number(s.unit_rate) || 0) * (Number(s.quantity) || 0);
                                          return sum + gross;
                                        }, 0);
                                        setFieldValue('package_details.total_service_amount', Number(newTotalServiceAmount.toFixed(2)));
                                      }
                                      setTimeout(() => {
                                        setInputValueForService('');
                                        setSelectedService(null);
                                      }, 0);
                                    }
                                  }}
                                  onInputChange={(e, value) => {
                                    setInputValueForService(value);
                                    setServiceSearch(value);
                                  }}
                                  getOptionLabel={option => option.name}

                                  renderInput={params => (
                                    <TextField
                                      {...params}
                                      label={tBilling('package_details.search_service')}
                                      placeholder={tBilling('package_details.type_to_search')}
                                      size="small"
                                    />
                                  )}
                                  noOptionsText={inputValueForService.trim() && PackageServicesList.length === 0 ? tBilling('package_details.no_related_option_for_search') : ''}
                                />
                              </Grid2>
                            </Grid2>

                            <Grid2 container spacing={2} sx={{ my: 2, }}>
                              <Grid2 size={{ lg: 3, md: 6, xs: 12 }}>
                                <TextField
                                  data-testid="package-concession-percent-input"
                                  label={tBilling('package_details.concession_percent') || 'Concession (%)'}
                                  fullWidth
                                  size="small"
                                  type="number"
                                  disabled={values.package_details.is_finalized || isOpenPackageSelected || !values.package_details.service_amount}
                                  sx={(values.package_details.is_finalized || isOpenPackageSelected) ? {
                                    backgroundColor: '#fafafa',
                                    opacity: 0.9,
                                    transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                  } : undefined}
                                  value={concessionPercentInput || (() => {
                                    // Show package-level concession percent (editable part)
                                    const concessionAmt = Number(values.package_details.package_concession_amount) || 0;
                                    const srvAmt = Number(values.package_details.service_amount) || 0;
                                    return srvAmt > 0 ? ((concessionAmt / srvAmt) * 100).toFixed(2) : '0.00';
                                  })()}
                                  onChange={(e) => {
                                    const inputVal = e.target.value;
                                    setConcessionPercentInput(inputVal);
                                    const percent = Number(inputVal) || 0;
                                    const srvAmt = Number(values.package_details.service_amount) || 0;
                                    const newAmount = srvAmt > 0 ? (srvAmt * percent) / 100 : 0;
                                    const roundedAmount = Number(newAmount.toFixed(2));
                                    setFieldValue('package_details.package_concession_amount', roundedAmount);
                          
                                    // Distribute package-level concession to ALL services proportionally
                                    // This preserves individual_discount_amount (extra service concession)
                                    const updatedServices = distributeConcessionToServices(values.service_items || [], roundedAmount);
                                    setFieldValue('service_items', updatedServices);
                          
                                    // Also update total_service_amount using gross amounts (rate * qty)
                                    const newTotalServiceAmount = updatedServices.reduce((sum, s) => {
                                      const gross = (Number(s.unit_rate) || 0) * (Number(s.quantity) || 0);
                                      return sum + gross;
                                    }, 0);
                                    setFieldValue('package_details.total_service_amount', Number(newTotalServiceAmount.toFixed(2)));
                                  }}
                                  onBlur={() => {
                                    // On blur, reset the local state so it syncs with the calculated value
                                    setConcessionPercentInput('');
                                  }}
                                  slotProps={{
                                    htmlInput: { min: 0, max: 100, step: 0.01 },
                                    input: { endAdornment: <span>%</span> }
                                  }}
                                  error={
                                    isConcessionOutOfRange ||
                          (touched.package_details?.package_concession_amount &&
                          Boolean(errors.package_details?.package_concession_amount))
                                  }
                                  helperText={concessionValidationMessage}
                                />
                              </Grid2>
                              <Grid2 size={{ lg: 3, md: 6, xs: 12 }}>
                                <TextField
                                  data-testid="package-concession-amount-input"
                                  label={tBilling('package_details.concession_amount') || 'Concession Amount'}
                                  fullWidth
                                  size="small"
                                  type="number"
                                  disabled={values.package_details.is_finalized || isOpenPackageSelected || !values.package_details.service_amount}
                                  sx={(values.package_details.is_finalized || isOpenPackageSelected) ? {
                                    backgroundColor: '#fafafa',
                                    opacity: 0.9,
                                    transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                  } : undefined}
                                  name="package_details.package_concession_amount"
                                  value={values.package_details.package_concession_amount || 0}
                                  onChange={(e) => {
                                    setConcessionPercentInput(''); // Clear percent input so it syncs
                                    const newAmount = Number(e.target.value) || 0;
                                    setFieldValue('package_details.package_concession_amount', newAmount);
                          
                                    // Distribute package-level concession to ALL services proportionally
                                    // This preserves individual_discount_amount (extra service concession)
                                    const updatedServices = distributeConcessionToServices(values.service_items || [], newAmount);
                                    setFieldValue('service_items', updatedServices);
                          
                                    // Also update total_service_amount using gross amounts (rate * qty)
                                    const newTotalServiceAmount = updatedServices.reduce((sum, s) => {
                                      const gross = (Number(s.unit_rate) || 0) * (Number(s.quantity) || 0);
                                      return sum + gross;
                                    }, 0);
                                    setFieldValue('package_details.total_service_amount', Number(newTotalServiceAmount.toFixed(2)));
                                  }}
                                  error={
                                    isConcessionOutOfRange ||
                          (touched.package_details?.package_concession_amount &&
                          Boolean(errors.package_details?.package_concession_amount))
                                  }
                                  helperText={totalExtraServiceConcession > 0 ? tPackage('package_details.concession_breakdown', '+ {{serviceLevel}} service-level = {{total}} total', {
                                    serviceLevel: `${currencySymbol}${totalExtraServiceConcession.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                                    total: `${currencySymbol}${totalConcessionDisplay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                                  }) : undefined}
                                  slotProps={{
                                    htmlInput: { min: 0 },
                                    formHelperText: { sx: { fontSize: '0.85rem', fontWeight: 500 } }
                                  }}
                                />
                              </Grid2>
                              <Grid2 size={{ lg: 3, md: 6, xs: 12 }}>
                                <TextField
                                  data-testid="package-net-service-amount-input"
                                  label={tBilling('package_details.net_service_amount') || 'Net Service Amount'}
                                  fullWidth
                                  size="small"
                                  type="number"
                                  disabled
                                  sx={{
                                    backgroundColor: '#fafafa',
                                    opacity: 0.9,
                                    transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                  }}
                                  value={((Number(values.package_details.service_amount) || 0) - (Number(values.package_details.package_concession_amount) || 0)).toFixed(2)}
                                  slotProps={{
                                    input: { readOnly: true }
                                  }}
                                />
                              </Grid2>
                            </Grid2>
                            {/* Service Name Header with badges and total */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f5f5f5', borderRadius: 1, px: 2, py: 1, mt: 3, mb: 1 }}>
                              <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#444', textWrap: 'nowrap' }}>{tBilling('package_details.service_name')}</Typography>
                              <ServiceSummaryRow
                                totalServices={MetaForStatus?.total_count ?? 0}
                                prescribedServices={MetaForStatus?.doctor_prescribed_only_count ?? 0}
                                matchedServices={MetaForStatus?.matched_count ?? 0}
                                otherServices={(MetaForStatus?.total_count ?? 0) - (MetaForStatus?.matched_count ?? 0)}
                                totalAmount={values.package_details.total_service_amount || 0}
                              />
                            </Box>

                            {/* Adjustable Head, Is Consumables Row - Package Level */}
                            <Grid2 container spacing={2} sx={{ alignItems: 'flex-start', my: 2 }}>
                              <Grid2 size={{ xl: 'auto', lg: 'auto', md: 'auto', xs: 12 }} sx={{ display: 'flex', alignItems: 'center', minHeight: 40,  }}>
                                <FormControlLabel
                                  data-testid="package-adjustable-head-checkbox"
                                  control={
                                    <Checkbox
                                      name="package_details.adjustable_head"
                                      color="success"
                                      icon={<span className={styles.AutoCompleteDropDownIcon} />}
                                      checkedIcon={<span className={styles.AutoCompleteDropDowncheckedIcon}>✔</span>}
                                      disabled={values.package_details.is_finalized || isOpenPackageSelected}
                                      checked={values.package_details.adjustable_head || false}
                                      onChange={(e) => {
                                        setFieldValue('package_details.adjustable_head', e.target.checked);
                                      
                                      }}
                                    />
                                  }
                                  label={tBilling('package_details.adjustable_head')}
                                />
                              </Grid2>
                              <Grid2 size={{ xl: 'auto', lg: 'auto', md: 'auto', xs: 12 }} sx={{ display: 'flex', alignItems: 'center', minHeight: 40,  }}>
                                <FormControlLabel
                                  data-testid="package-is-consumables-checkbox"
                                  control={
                                    <Checkbox
                                      name="package_details.is_consumables"
                                      color="success"
                                      icon={<span className={styles.AutoCompleteDropDownIcon} />}
                                      checkedIcon={<span className={styles.AutoCompleteDropDowncheckedIcon}>✔</span>}
                                      disabled={values.package_details.is_finalized || isOpenPackageSelected}
                                      checked={values.package_details.is_consumables || false}
                                      onChange={(e) => {
                                        setFieldValue('package_details.is_consumables', e.target.checked);
                                        if (!e.target.checked) {
                                          setFieldValue('package_details.consumable_amount', 0);
                                        }
                                      }}
                                    />
                                  }
                                  label={tBilling('package_details.is_consumables')}
                                />
                              </Grid2>
                              <Grid2 size={{ xl: 2, lg: 2, md: 3, xs: 12 }}>
                                <TextField
                                  data-testid="package-consumable-amount-input"
                                  name="package_details.consumable_amount"
                                  label={tBilling('package_details.enter_amount')}
                                  placeholder={tBilling('package_details.enter_amount')}
                                  size="small"
                                  type="number"
                                  fullWidth
                                  disabled={values.package_details.is_finalized || isOpenPackageSelected || !values.package_details.is_consumables}
                                  value={values.package_details.consumable_amount || ''}
                                  style={{ cursor: !values.package_details.is_consumables ? 'no-drop' : 'auto' }}
                                  onChange={(e) => setFieldValue('package_details.consumable_amount', Number(e.target.value) || 0)}
                                />
                              </Grid2>

                            </Grid2>

                            {values.service_items && values.service_items.length > 0 ? (
                              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#f6f5f540', p: 2 }}>
                                {values.service_items.map((item: ServiceItem, idx: number) => {
                                  const serviceItemErrors = (errors.service_items?.[idx] ?? {}) as FormikErrors<ServiceItem>;
                                  const serviceItemTouched = (touched.service_items?.[idx] ?? {}) as FormikTouched<ServiceItem>;
                                  
                                  // Gross Amount = rate * qty (synced via FormValuesSync)
                                  const rawGrossAmount = (Number(item.unit_rate) || 0) * (Number(item.quantity) || 0);
                                  const fixedGross = Number(rawGrossAmount.toFixed(2));
                                  
                                  const unitRate = Number(item.unit_rate) || 0;
                                  const storedDiscountAmount = Number(item.discount_amount) || 0;
                                  
                                  // Effective discount percent per unit (synced via FormValuesSync)
                                  const effectiveDiscountPct = unitRate > 0 
                                    ? Number(((storedDiscountAmount / unitRate) * 100).toFixed(2)) 
                                    : 0;
                                  
                                  // Net Amount = gross - (discount_amount * qty) (synced via FormValuesSync)
                                  const finalDiscountAmount = Number((storedDiscountAmount * (Number(item.quantity) || 0)).toFixed(2));
                                  const fixedTotal = Number((fixedGross - finalDiscountAmount).toFixed(2));

                                  return (
                                    <React.Fragment key={idx}>
                                      {/* Service Card Header */}
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                                          <Typography variant="body1" sx={{ py: 1, px: 0 }}>
                                            {item.service_name || <span style={{ color: '#aaa' }}>{tBilling('package_details.select_service')}</span>}
                                            {item.is_prescribed_from_doctor && (
                                              <span style={{ color: '#e57353', fontWeight: 600, fontSize: 14 }}>
                                                (Rx)
                                              </span>
                                            )}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                                            {/* Service From No. of Cycles badge */}
                                            {item.procedure_details && item.procedure_details.length > 0 && (
                                              <>
                                                <Tooltip title={tBilling('package_details.click_to_see_cycle_details', { defaultValue: 'Click to see cycle details' })}>
                                                  <Box
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label={tBilling('package_details.click_to_see_cycle_details', { defaultValue: 'Click to see cycle details' })}
                                                    onClick={(e) => handleCyclePopoverOpen(e, idx)}
                                                    onKeyDown={(e) => handleCyclePopoverKeyDown(e, idx)}
                                                    sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 0.5,
                                                      px: 1.5,
                                                      py: 0.5,
                                                      borderRadius: 2,
                                                      bgcolor: '#e3f2fd',
                                                      cursor: 'pointer',
                                                      '&:hover': { bgcolor: '#bbdefb' },
                                                      '&:focus': { outline: '2px solid #1976d2', outlineOffset: 2 },
                                                    }}
                                                  >
                                                    <Typography sx={{ fontSize: 12, color: '#1976d2', fontWeight: 500 }}>
                                                      {tBilling('package_details.service_from_cycles', { defaultValue: 'Service From No. of Cycles' })}:
                                                    </Typography>
                                                    <Box
                                                      sx={{
                                                        minWidth: 20,
                                                        height: 20,
                                                        borderRadius: '50%',
                                                        bgcolor: '#1976d2',
                                                        color: '#fff',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                      }}
                                                    >
                                                      {item.procedure_details.length}
                                                    </Box>
                                                  </Box>
                                                </Tooltip>
                                                <Popover
                                                  open={cyclePopoverAnchor.element !== null && cyclePopoverAnchor.serviceIdx === idx}
                                                  anchorEl={cyclePopoverAnchor.element}
                                                  onClose={handleCyclePopoverClose}
                                                  anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'right',
                                                  }}
                                                  transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'right',
                                                  }}
                                                  PaperProps={{
                                                    sx: {
                                                      backgroundColor: '#fff',
                                                      color: '#222',
                                                      borderRadius: 2,
                                                      minWidth: 200,
                                                      maxWidth: 320,
                                                      p: 1,
                                                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                                    },
                                                  }}
                                                >
                                                  <Typography sx={{ fontSize: 12, color: '#1976d2', px: 1, pt: 0.5 }}>
                                                    {tBilling('package_details.cycle_details', { defaultValue: 'CYCLE DETAILS' })} ({item.procedure_details.length})
                                                  </Typography>
                                                  <List dense sx={{ py: 0 }}>
                                                    {item.procedure_details.map((pd, i) => (
                                                      <ListItem key={i} sx={{ py: 0.5 }}>
                                                        <ListItemText
                                                          primary={pd.procedure_source_name || ''}
                                                          primaryTypographyProps={{ sx: { fontSize: 14, color: '#222' } }}
                                                        />
                                                      </ListItem>
                                                    ))}
                                                  </List>
                                                </Popover>
                                              </>
                                            )}
                                            <Typography sx={{ fontWeight: 500, fontSize: 14, color: '#444' }}>
                                              {tBilling('package_details.gross_service_amount')}: {currencySymbol}{fixedGross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                              {' | '}
                                              {tBilling('package_details.net_service_amount')}: {currencySymbol}{fixedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </Typography>
                                          </Box>
                                          {finalDiscountAmount > 0 && (() => {
                                            const quantity = Number(item.quantity) || 0;
                                            const distributedTotal = Number(((Number(item.distributed_concession_amount) || 0) * quantity).toFixed(2));
                                            const manualTotal = Number(((Number(item.individual_discount_amount) || 0) * quantity).toFixed(2));
                                            return (
                                              <Typography sx={{ fontSize: 12, color: '#3b82f6' }}>
                                                {tBilling('package_details.total_discount')}: {currencySymbol}{finalDiscountAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({effectiveDiscountPct}%)
                                                {manualTotal > 0 && (
                                                  <span style={{ color: '#f97316', marginLeft: 8 }}>
                                                    (Manual: {currencySymbol}{manualTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} + Package: {currencySymbol}{distributedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                                                  </span>
                                                )}
                                              </Typography>
                                            );
                                          })()}
                                        </Box>
                                      </Box>

                                      {/* Unlimited Quantity Checkbox */}
                                      <Box sx={{ mb: 2 }}>
                                        <FormControlLabel
                                          data-testid={`package-service-${idx}-unlimited-qty-checkbox`}
                                          control={
                                            <Checkbox
                                              name={`service_items[${idx}].unlimited_qty`}
                                              color="success"
                                              icon={<span className={styles.AutoCompleteDropDownIcon} />}
                                              checkedIcon={<span className={styles.AutoCompleteDropDowncheckedIcon}>✔</span>}
                                              disabled={values.package_details.is_finalized || isOpenPackageSelected}
                                              checked={item.unlimited_qty || false}
                                              onChange={(e) => {
                                                handleChange(e);
                                                if (e.target.checked) {
                                                  // Store original values before setting to 0
                                                  if (!item.original_unit_rate) {
                                                    setFieldValue(`service_items[${idx}].original_unit_rate`, item.unit_rate);
                                                  }
                                                  if (!item.original_quantity) {
                                                    setFieldValue(`service_items[${idx}].original_quantity`, item.quantity);
                                                  }
                                                  setFieldValue(`service_items[${idx}].quantity`, 0);
                                                  setFieldValue(`service_items[${idx}].discount_percent`, 0);
                                                  setFieldValue(`service_items[${idx}].discount_amount`, 0);
                                                  setFieldValue(`service_items[${idx}].individual_discount_amount`, 0);
                                                  setFieldValue(`service_items[${idx}].individual_discount_percent`, 0);
                                                  setFieldValue(`service_items[${idx}].distributed_concession_amount`, 0);
                                                  setFieldValue(`service_items[${idx}].unit_rate`, 0);
                                                  setFieldValue(`service_items[${idx}].unit_rate_percentage`, 0);
                                                } else {
                                                  // Restore quantity and unit_rate to original values
                                                  const originalQty = item.original_quantity ?? 1;
                                                  const originalRate = item.original_unit_rate ?? 0;
                                                  setFieldValue(`service_items[${idx}].quantity`, originalQty);
                                                  setFieldValue(`service_items[${idx}].unit_rate`, originalRate);
                                                }
                                              }}
                                            />
                                          }
                                          label={tBilling('package_details.unlimited_quantity')}
                                        />
                                      </Box>

                                      {/* Service Form Fields Row */}
                                      <Grid2 container spacing={2} sx={{ mb: 2 }}>
                                        <Grid2 size={{ xl: 2, lg: 2, md: 3, xs: 6 }}>
                                          <Autocomplete
                                            data-testid={`package-service-${idx}-procedure-select`}
                                            options={billingConfig?.PACKAGE_PROCEDURE_MASTER || []}
                                            getOptionLabel={option => String(option.name)}
                                            disabled={values.package_details.is_finalized || isOpenPackageSelected}
                                            sx={(values.package_details.is_finalized || isOpenPackageSelected) ? {
                                              backgroundColor: '#fafafa',
                                              opacity: 0.9,
                                            } : undefined}
                                            value={billingConfig?.PACKAGE_PROCEDURE_MASTER?.find(opt => opt.code === item.procedure) || null}
                                            onChange={(_, newValue) => setFieldValue(`service_items[${idx}].procedure`, newValue ? newValue.code : '')}
                                            renderInput={params => (
                                              <TextField
                                                {...params}
                                                label={tBilling('package_details.procedure')}
                                                size="small"
                                                error={Boolean(serviceItemTouched.procedure && serviceItemErrors.procedure)}
                                              />
                                            )}
                                            isOptionEqualToValue={(option, value) => option.code === value.code}
                                          />
                                        </Grid2>
                                        <Grid2 size={{ xl: 1.8, lg: 2, md: 3, xs: 6 }}>
                                          <Autocomplete
                                            data-testid={`package-service-${idx}-gender-select`}
                                            options={[
                                              { value: 'both', label: 'Both (M&F)' },
                                              { value: 'male', label: 'Male' },
                                              { value: 'female', label: 'Female' },
                                            ]}
                                            getOptionLabel={option => option.label}
                                            value={(() => {
                                              const opts = [
                                                { value: 'both', label: 'Both (M&F)' },
                                                { value: 'male', label: 'Male' },
                                                { value: 'female', label: 'Female' },
                                              ];
                                              return opts.find(opt => opt.value === item.gender) || null;
                                            })()}
                                            onChange={(_, newValue) => setFieldValue(`service_items[${idx}].gender`, newValue ? newValue.value : '')}
                                            disabled={values.package_details.is_finalized || isOpenPackageSelected}
                                            sx={(values.package_details.is_finalized || isOpenPackageSelected) ? {
                                              backgroundColor: '#fafafa',
                                              opacity: 0.9,
                                            } : undefined}
                                            renderInput={params => (
                                              <TextField
                                                {...params}
                                                label={tBilling('package_details.gender')}
                                                size="small"
                                                error={Boolean(serviceItemTouched.gender && serviceItemErrors.gender)}
                                              />
                                            )}
                                            isOptionEqualToValue={(option, value) => option.value === value.value}
                                          />
                                        </Grid2>
                                        <Grid2 size={{ xl: 1.4, lg: 1.5, md: 2, xs: 6 }}>
                                          <TextField
                                            data-testid={`package-service-${idx}-discount-percent-input`}
                                            fullWidth
                                            size="small"
                                            label={`${tBilling('package_details.discount')} (%)`}
                                            type="number"
                                            value={item.discount_percent || 0}
                                            name={`service_items[${idx}].discount_percent`}
                                            slotProps={{ htmlInput: { min: 0, max: 100, step: 'any', readOnly: true } }}
                                            disabled={true}
                                            sx={{ backgroundColor: '#f5f5f5' }}
                                            error={Boolean(serviceItemTouched.discount_percent && serviceItemErrors.discount_percent)}
                                          />
                                        </Grid2>
                                        <Grid2 size={{ xl: 1.4, lg: 1.5, md: 2, xs: 6 }}>
                                          <TextField
                                            data-testid={`package-service-${idx}-discount-amount-input`}
                                            fullWidth
                                            size="small"
                                            label={tBilling('package_details.discount_amount_unit')}
                                            type="number"
                                            value={item.discount_amount || 0}
                                            name={`service_items[${idx}].discount_amount`}
                                            onChange={e => {
                                              const newCombinedAmt = Number(e.target.value) || 0;
                                              const unitRate = Number(item.unit_rate) || 0;
                                              const qty = Number(item.quantity) || 0;
                                              const distributedAmt = Number(item.distributed_concession_amount) || 0;
                                              
                                              // Individual discount = combined - distributed
                                              const individualDiscountAmt = Math.max(0, Number((newCombinedAmt - distributedAmt).toFixed(2)));
                                              const individualDiscountPct = unitRate > 0 ? Number(((individualDiscountAmt / unitRate) * 100).toFixed(2)) : 0;
                                              
                                              // Store individual discount values
                                              setFieldValue(`service_items[${idx}].individual_discount_amount`, individualDiscountAmt);
                                              setFieldValue(`service_items[${idx}].individual_discount_percent`, individualDiscountPct);
                                              
                                              // Store effective combined discount amount (not percent - share % is calculated in render)
                                              setFieldValue(`service_items[${idx}].discount_amount`, newCombinedAmt);
                                              
                                              // Calculate total_amount for this item (net amount)
                                              const newTotalAmount = Number((((unitRate - newCombinedAmt) * qty)).toFixed(2));
                                              setFieldValue(`service_items[${idx}].total_amount`, newTotalAmount);
                                              
                                              // Calculate gross_amount for this item
                                              const newGrossAmount = Number((unitRate * qty).toFixed(2));
                                              setFieldValue(`service_items[${idx}].gross_amount`, newGrossAmount);
                                              
                                              // Recalculate total_service_amount using gross amounts (rate * qty)
                                              const updatedItems = values.service_items.map((s, i) => 
                                                i === idx ? { ...s, gross_amount: newGrossAmount } : s
                                              );
                                              const newTotalServiceAmount = updatedItems.reduce((sum, s) => {
                                                const gross = (Number(s.unit_rate) || 0) * (Number(s.quantity) || 0);
                                                return sum + gross;
                                              }, 0);
                                              setFieldValue('package_details.total_service_amount', Number(newTotalServiceAmount.toFixed(2)));
                                            }}
                                            slotProps={{ htmlInput: { min: 0, step: 'any' } }}
                                            disabled={values.package_details.is_finalized || isOpenPackageSelected}
                                            error={Boolean(serviceItemTouched.discount_amount && serviceItemErrors.discount_amount)}
                                          />
                                        </Grid2>

                                        <Grid2 size={{ xl: 1.7, lg: 2, md: 2, xs: 6 }}>
                                          <TextField
                                            data-testid={`package-service-${idx}-unit-rate-input`}
                                            fullWidth
                                            size="small"
                                            label={`${tBilling('package_details.unit_rate')}(${currencySymbol})`}
                                            type="number"
                                            value={item.unit_rate}
                                            name={`service_items[${idx}].unit_rate`}
                                            slotProps={{ htmlInput: { min: 0 } }}
                                            disabled={values.package_details.is_finalized || isOpenPackageSelected}
                                            onChange={e => {
                                              if (values.package_details.is_finalized || isOpenPackageSelected) return;
                                              handleChange(e);
                                              const newRate = Number(e.target.value) || 0;
                                              const qty = Number(item.quantity) || 1;
                                              const pkgConcession = Number(values.package_details.package_concession_amount) || 0;
                                              
                                              if (servAmt > 0 && qty > 0) {
                                                const percent = (newRate * 100) / servAmt;
                                                setFieldValue(`service_items[${idx}].unit_rate_percentage`, Number(percent.toFixed(2)));
                                              }
                                              
                                              // Also update individual_discount_amount based on individual_discount_percent if rate changes
                                              const individualDiscountPct = Number(item.individual_discount_percent) || 0;
                                              const newIndividualDiscountAmt = newRate > 0 ? Number(((newRate * individualDiscountPct) / 100).toFixed(2)) : 0;
                                              
                                              // Update unit_rate and individual_discount_amount, then redistribute package concession
                                              const itemsWithNewRate = values.service_items.map((s, i) => 
                                                i === idx ? { ...s, unit_rate: newRate, individual_discount_amount: newIndividualDiscountAmt } : s
                                              );
                                              
                                              // Redistribute package concession with updated rates
                                              const redistributed = distributeConcessionToServices(itemsWithNewRate, pkgConcession);
                                              setFieldValue('service_items', redistributed);
                                              
                                              // Recalculate total_service_amount using gross amounts (rate * qty)
                                              const newTotalServiceAmount = redistributed.reduce((sum, s) => {
                                                const gross = (Number(s.unit_rate) || 0) * (Number(s.quantity) || 0);
                                                return sum + gross;
                                              }, 0);
                                              setFieldValue('package_details.total_service_amount', Number(newTotalServiceAmount.toFixed(2)));
                                            }}
                                            error={Boolean(serviceItemTouched.unit_rate && serviceItemErrors.unit_rate)}
                                          />
                                        </Grid2>
                                        <Grid2 size={{ xl: 1.4, lg: 1.5, md: 2, xs: 6 }}>
                                          <TextField
                                            data-testid={`package-service-${idx}-quantity-input`}
                                            fullWidth
                                            size="small"
                                            label={tBilling('package_details.quantity')}
                                            type="number"
                                            value={item.quantity}
                                            name={`service_items[${idx}].quantity`}
                                            onChange={e => {
                                              handleChange(e);
                                              const newQty = Number(e.target.value) || 0;
                                              const pkgConcession = Number(values.package_details.package_concession_amount) || 0;
                                              
                                              // Update quantity first, then redistribute package concession
                                              const itemsWithNewQty = values.service_items.map((s, i) => 
                                                i === idx ? { ...s, quantity: newQty } : s
                                              );
                                              
                                              // Redistribute package concession with updated quantities
                                              const redistributed = distributeConcessionToServices(itemsWithNewQty, pkgConcession);
                                              setFieldValue('service_items', redistributed);
                                              
                                              // Recalculate total_service_amount using gross amounts (rate * qty)
                                              const newTotalServiceAmount = redistributed.reduce((sum, s) => {
                                                const gross = (Number(s.unit_rate) || 0) * (Number(s.quantity) || 0);
                                                return sum + gross;
                                              }, 0);
                                              setFieldValue('package_details.total_service_amount', Number(newTotalServiceAmount.toFixed(2)));
                                            }}
                                            slotProps={{ htmlInput: { min: 1 } }}
                                            disabled={values.package_details.is_finalized || isOpenPackageSelected || item.unlimited_qty}
                                            error={Boolean(serviceItemTouched.quantity && serviceItemErrors.quantity)}
                                          />
                                        </Grid2>
                                        <Grid2 size={{ xl: 1.4, lg: 1.5, md: 2, xs: 6 }}>
                                          <TextField
                                            data-testid={`package-service-${idx}-unit-percent-input`}
                                            fullWidth
                                            size="small"
                                            label={tBilling('package_details.unit_percentage')}
                                            type="number"
                                            value={
                                              isAmount
                                                ? (servAmt > 0
                                                  ? ((Number(item.unit_rate) / servAmt) * 100).toFixed(2)
                                                  : (item.unit_rate_percentage ?? 0).toString())
                                                : (item.unit_rate_percentage ?? '').toString()
                                            }
                                            name={`service_items[${idx}].unit_rate_percentage`}
                                            slotProps={{
                                              htmlInput: { min: 0, max: 100, step: 'any' },
                                              input: { sx: { color: '#000' } }
                                            }}
                                            disabled={isAmount || values.package_details.is_finalized || isOpenPackageSelected}
                                            onChange={e => {
                                              if (isAmount || values.package_details.is_finalized || isOpenPackageSelected) return;
                                              const val = Number(e.target.value);
                                              const safeVal = Number.isNaN(val) ? 0 : val;
                                              setFieldValue(`service_items[${idx}].unit_rate_percentage`, safeVal);
                                              const qty = Number(item.quantity) || 1;
                                              const pkgConcession = Number(values.package_details.package_concession_amount) || 0;
                                              
                                              let newRate = 0;
                                              if (servAmt > 0 && qty > 0) {
                                                newRate = Number(((servAmt * safeVal) / (100 * qty)).toFixed(2));
                                                setFieldValue(`service_items[${idx}].unit_rate`, newRate);
                                              }
                                              
                                              // Also update individual_discount_amount based on individual_discount_percent if rate changes
                                              const individualDiscountPct = Number(item.individual_discount_percent) || 0;
                                              const newIndividualDiscountAmt = newRate > 0 ? Number(((newRate * individualDiscountPct) / 100).toFixed(2)) : 0;
                                              
                                              // Update unit_rate and individual_discount_amount, then redistribute package concession
                                              const itemsWithNewRate = values.service_items.map((s, i) => 
                                                i === idx ? { ...s, unit_rate: newRate, individual_discount_amount: newIndividualDiscountAmt } : s
                                              );
                                              
                                              // Redistribute package concession with updated rates
                                              const redistributed = distributeConcessionToServices(itemsWithNewRate, pkgConcession);
                                              setFieldValue('service_items', redistributed);
                                              
                                              // Recalculate total_service_amount using gross amounts (rate * qty)
                                              const newTotalServiceAmount = redistributed.reduce((sum, s) => {
                                                const gross = (Number(s.unit_rate) || 0) * (Number(s.quantity) || 0);
                                                return sum + gross;
                                              }, 0);
                                              setFieldValue('package_details.total_service_amount', Number(newTotalServiceAmount.toFixed(2)));
                                            }}
                                            error={Boolean(serviceItemTouched.unit_rate_percentage && serviceItemErrors.unit_rate_percentage)}
                                          />
                                        </Grid2>
                                        <Grid2 size={{ xl: 0.8, lg: 1, md: 1, xs: 6 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
                                          <IconButton data-testid={`package-service-${idx}-delete-button`} disabled={isOpenPackageSelected} onClick={() => {
                                            handleDeleteClick(() => {
                                              const pkgConcession = Number(values.package_details.package_concession_amount) || 0;
                                              
                                              if (idx === 0 && values.service_items.length === 1) {
                                                // Clear the only service item but keep it as empty placeholder
                                                const emptyServiceItem: ServiceItem = {
                                                  service: 0,
                                                  service_name: '',
                                                  service_source_type: '',
                                                  procedure: '',
                                                  category: '',
                                                  gender: 'both',
                                                  discount_percent: 0,
                                                  discount_amount: 0,
                                                  individual_discount_amount: 0,
                                                  individual_discount_percent: 0,
                                                  distributed_concession_amount: 0,
                                                  discount_on_quantity: false,
                                                  unlimited_qty: false,
                                                  quantity: 1,
                                                  unit_rate: 0,
                                                  original_unit_rate: 0,
                                                  original_quantity: 1,
                                                  unit_rate_percentage: 0,
                                                  gross_amount: 0,
                                                  total_amount: 0,
                                                  is_from_package: false,
                                                  is_prescribed_from_doctor: false,
                                                  procedure_details: []
                                                };
                                                setFieldValue('service_items', [emptyServiceItem]);
                                                setFieldValue('package_details.total_service_amount', 0);
                                              } else {
                                                // Remove the service and redistribute concession
                                                const updatedServices = values.service_items.filter((_, i) => i !== idx);
                                                const redistributed = distributeConcessionToServices(updatedServices, pkgConcession);
                                                setFieldValue('service_items', redistributed);
                                                
                                                // Update total_service_amount
                                                const newTotalServiceAmount = redistributed.reduce((sum, s) => {
                                                  const gross = (Number(s.unit_rate) || 0) * (Number(s.quantity) || 0);
                                                  return sum + gross;
                                                }, 0);
                                                setFieldValue('package_details.total_service_amount', Number(newTotalServiceAmount.toFixed(2)));
                                              }
                                            });
                                          }}>
                                            <DeleteIcon />
                                          </IconButton>
                                        </Grid2>


                                      </Grid2>

                                      {idx < values.service_items.length - 1 && (
                                        <Divider sx={{ borderColor: '#6a6868ff', borderBottomWidth: 1.5, my: 2 }} />
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </Box>
                            ) : (
                              <Typography color="text.secondary" sx={{ ml: 1 }}>{tBilling('package_details.no_services_added')}</Typography>
                            )}
                          </>
                        );
                      }
                      }
                    </FieldArray>
                  </Box>
                )}

                {/* Pharmacy Items Section */}

                {activeTab === 'pharmacy' && (
                  <Box >
                    <FieldArray name="pharmacy_items">
                      {({ push, remove }) => {
                        // Pharmacy list from API
                        const pharmacyItems = Array.isArray(pharmacyList?.objects) ? pharmacyList.objects : [];
                        const isPharmacyDisabled = values.package_details.is_finalized || isOpenPackageSelected || values.package_details.pharmacy_set_all_items;
                        
                        return (
                          <>
                            <Box sx={{my: 3}}>

                            
                              <Grid2 container spacing={2} alignItems="center" justifyContent="space-between">
                                {/* Left side: Set All Items checkbox */}
                                <Grid2 size={4}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={values.package_details.pharmacy_set_all_items || false}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          setFieldValue('package_details.pharmacy_set_all_items', checked);
                                          if (checked) {
                                            // Save current pharmacy items before clearing
                                            const currentItems = values.pharmacy_items ?? [];
                                            const hasValidItems = currentItems.some(item => item.item && item.item !== 0);
                                            if (hasValidItems) {
                                              setSavedPharmacyItems(currentItems);
                                            }
                                            // Reset pharmacy items to a single blank row
                                            setFieldValue('pharmacy_items', [{
                                              item: 0,
                                              item_name: '',
                                              quantity: 0,
                                              uom: null,
                                              discount_percent: 0,
                                              uom_dropdown: null,
                                            }]);
                                            // Clear selections
                                            setSelectedDrug(null);
                                            setInputValue('');
                                            setSelectedItemGroupCode('');
                                            setSelectedItemCategoryCode('');
                                          } else {
                                            // Restore saved pharmacy items if available
                                            if (savedPharmacyItems && savedPharmacyItems.length > 0) {
                                              setFieldValue('pharmacy_items', savedPharmacyItems);
                                              setSavedPharmacyItems(null);
                                            }
                                          }
                                        }}
                                        icon={<span className={styles.AutoCompleteDropDownIcon} />}
                                        checkedIcon={<span className={styles.AutoCompleteDropDowncheckedIcon}>✔</span>}
                                        disabled={values.package_details.is_finalized || isOpenPackageSelected}
                                      />
                                    }
                                    label={tBilling('package_details.set_all_items')}
                                  />
                                </Grid2>
                                {/* Right side: Dropdowns */}
                                <Grid2  size={8} container spacing={2} alignItems="center" justifyContent="flex-end">
                                  <Grid2 size={{ md:4, xs:12}}>
                                    <Autocomplete
                                      data-testid="package-item-group-select"
                                      disabled={isPharmacyDisabled}
                                      sx={isPharmacyDisabled ? {
                                        backgroundColor: '#fafafa',
                                        opacity: 0.9,
                                        transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                      } : undefined}
                                      fullWidth
                                      size="small"
                                      options={billingConfig?.item_group || []}
                                      getOptionLabel={option => option.name}
                                      value={billingConfig?.item_group?.find(opt => opt.code === selectedItemGroupCode) || null}
                                      onChange={(_e, newValue) => setSelectedItemGroupCode(newValue ? newValue.code : '')}
                                      renderInput={params => (
                                        <TextField
                                          {...params}
                                          label={tBilling('package_details.item_group')}
                                          placeholder={tBilling('package_details.select_item_group')}
                                          size="small"
                                          sx={{ minWidth: 180 }}
                                        />
                                      )}
                                      isOptionEqualToValue={(option, value) => option.code === value.code}
                                    />
                                  </Grid2>
                                  <Grid2 size={{ md:4, xs:12}}>
                                    <Autocomplete
                                      data-testid="package-item-category-select"
                                      fullWidth
                                      size="small"
                                      disabled={isPharmacyDisabled}
                                      sx={isPharmacyDisabled ? {
                                        backgroundColor: '#fafafa',
                                        opacity: 0.9,
                                        transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                      } : undefined}
                                      options={billingConfig?.item_category || []}
                                      getOptionLabel={option => option.name}
                                      value={billingConfig?.item_category?.find(opt => opt.code === selectedItemCategoryCode) || null}
                                      onChange={(_e, newValue) => setSelectedItemCategoryCode(newValue ? newValue.code : '')}
                                      renderInput={params => (
                                        <TextField
                                          {...params}
                                          label={tBilling('package_details.item_category')}
                                          placeholder={tBilling('package_details.select_item_category')}
                                          size="small"
                                          sx={{ minWidth: 180 }}
                                        />
                                      )}
                                      isOptionEqualToValue={(option, value) => option.code === value.code}
                                    />
                                  </Grid2>
                                  <Grid2 size={{md:4, xs:12}}>
                                    <Autocomplete
                                      data-testid="package-search-drug-select"
                                      disablePortal
                                      fullWidth
                                      size="small"
                                      disabled={isPharmacyDisabled}
                                      sx={isPharmacyDisabled ? {
                                        backgroundColor: '#fafafa',
                                        opacity: 0.9,
                                        transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                      } : undefined}
                                      options={inputValue.trim() ? pharmacyItems : []}
                                      open={pharmacyItems.length > 0 && inputValue.length > 0 && !isPharmacyDisabled}
                                      value={selectedDrug}
                                      inputValue={inputValue}
                                      onChange={(e, value) => {
                                        setSelectedDrug(value);
                                        if (value) {
                                          const v = value as PharmacyItemType;
                                          // Store the selected item in ref to preserve its uom_conversion data
                                          selectedPharmacyItemsRef.current.set(v.id, v);
                                        
                                          // If first row is blank, update it
                                          if (
                                            values.pharmacy_items &&
                                          values.pharmacy_items.length > 0 &&
                                          !values.pharmacy_items[0].item_name
                                          ) {
                                            setFieldValue('pharmacy_items[0].item_name', v.name_label || v.name || '');
                                            setFieldValue('pharmacy_items[0].item', v.id );
                                            setFieldValue('pharmacy_items[0].quantity', 1);
                                            setFieldValue('pharmacy_items[0].uom', 'uom' in v && typeof v.uom === 'string' ? v.uom : '');
                                            setFieldValue('pharmacy_items[0].discount_percent', 0);
                                            setFieldValue('pharmacy_items[0].uom_dropdown', v.uom_conversion || null);
                                          } else {
                                            push({
                                              item: v.id,
                                              item_name: v.name_label || v.name || '',
                                              quantity: 1,
                                              uom: 'uom' in v && typeof v.uom === 'string' ? v.uom : '',
                                              discount_percent: 0,
                                              uom_dropdown: v.uom_conversion || null,
                                            });
                                          }
                                          setTimeout(() => {
                                            setInputValue('');
                                            setSelectedDrug(null);
                                          }, 0);
                                        }
                                      }}
                                      onInputChange={(e, value) => {
                                        setInputValue(value);
                                        setPharmacySearch(value);
                                        refetchPharmacyList();
                                      }}
                                      getOptionLabel={option => typeof option === 'string' ? option : option.name_label || option.name || ''}
                                      filterOptions={(options, state) =>
                                        options.filter(option => {
                                          const search = state.inputValue.toLowerCase();
                                          return (
                                            option.name_label?.toLowerCase().includes(search) ||
                                        option.name?.toLowerCase().includes(search) ||
                                        option.molecule?.toLowerCase().includes(search)
                                          );
                                        })
                                      }
                                      renderOption={(props, option) => (
                                        <li {...props}>
                                          {option.name_label} {option.molecule && `(${option.molecule})`}
                                        </li>
                                      )}
                                      renderInput={params => (
                                        <TextField
                                          {...params}
                                          label={tBilling('package_details.search_pharmacy_item')}
                                          placeholder={tBilling('package_details.type_to_search')}
                                          size="small"
                                        />
                                      )}
                                      noOptionsText={inputValue.trim() && pharmacyItems.length === 0 ? 'No related option for search' : ''}
                                    />
                                  </Grid2>
                                </Grid2>
                              </Grid2>
                            </Box>
                            <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 2 }}>
                              <Table>
                                <TableHead>
                                  <TableRow sx={{ backgroundColor: '#fafafa' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>{tBilling('package_details.item_name')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{tBilling('package_details.quantity')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{tBilling('package_details.uom')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{tBilling('package_details.discount')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>{tBilling('package_details.action')}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {values.pharmacy_items && values.pharmacy_items.length > 0 && values.pharmacy_items.map((item, idx) => (
                                    <TableRow key={idx}>
                                      {(() => {
                                        const pharmacyErrors = (errors.pharmacy_items?.[idx] ?? {}) as FormikErrors<PharmacyItem>;
                                        const pharmacyTouched = (touched.pharmacy_items?.[idx] ?? {}) as FormikTouched<PharmacyItem>;
                                        
                                        // Pharmacy item discount percent validation based on CONCESSION rule
                                        const itemDiscountPercent = Number(item.discount_percent) || 0;
                                        const isPharmacyDiscountBelowMin = concessionRule && itemDiscountPercent > 0 && itemDiscountPercent < concessionMinPercent;
                                        const isPharmacyDiscountAboveMax = concessionRule && itemDiscountPercent > concessionMaxPercent;
                                        const isPharmacyDiscountOutOfRange = isPharmacyDiscountBelowMin || isPharmacyDiscountAboveMax;
                                        const pharmacyDiscountValidationMessage = isPharmacyDiscountOutOfRange
                                          ? tBilling('package_details.concession_validation_error', {
                                            min: concessionMinPercent.toFixed(2),
                                            max: concessionMaxPercent.toFixed(2),
                                            defaultValue: `Discount must be between ${concessionMinPercent.toFixed(2)}% and ${concessionMaxPercent.toFixed(2)}%`,
                                          })
                                          : '';
                                        
                                        return (
                                          <>
                                            <TableCell>
                                              <TextField
                                                data-testid={`package-pharmacy-${idx}-name-input`}
                                                disabled
                                                sx={isPharmacyDisabled || values.package_details.is_finalized ? {
                                                  backgroundColor: '#fafafa',
                                                  opacity: 0.9,
                                                  transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                                } : undefined}
                                                fullWidth
                                                size="small"
                                                placeholder={`Item ${idx + 1}`}
                                                name={`pharmacy_items[${idx}].item_name`}
                                                value={item.item_name}
                                                onChange={handleChange}
                                              />
                                            </TableCell>
                                            <TableCell>
                                              <TextField
                                                data-testid={`package-pharmacy-${idx}-quantity-input`}
                                                fullWidth
                                                size="small"
                                                disabled={isPharmacyDisabled}
                                                sx={isPharmacyDisabled ? {
                                                  backgroundColor: '#fafafa',
                                                  opacity: 0.9,
                                                  transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                                } : undefined}
                                                placeholder={tBilling('package_config.placeholders.enter', { defaultValue: 'Enter' })}
                                                type="number"
                                                name={`pharmacy_items[${idx}].quantity`}
                                                value={item.quantity}
                                                onChange={handleChange}
                                                slotProps={{ htmlInput: { min: 1 } }}
                                                error={Boolean(pharmacyTouched.quantity && pharmacyErrors.quantity)}
                                              />
                                            </TableCell>
                                            <TableCell>
                                              <Autocomplete
                                                data-testid={`package-pharmacy-${idx}-uom-select`}
                                                disabled={isPharmacyDisabled}
                                                sx={isPharmacyDisabled ? {
                                                  backgroundColor: '#fafafa',
                                                  opacity: 0.9,
                                                  transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                                } : undefined}
                                                options={values.pharmacy_items[idx].uom_dropdown || []}
                                                getOptionLabel={option => option.from_uom_label}
                                                value={values?.pharmacy_items[idx]?.uom_dropdown?.find(opt => opt.from_uom === item.uom) || null}
                                                onChange={(_, newValue) => setFieldValue(`pharmacy_items[${idx}].uom`, newValue ? newValue.from_uom : '')}
                                                renderInput={params => (
                                                  <TextField
                                                    {...params}
                                                    placeholder={tBilling('package_details.select_uom')}
                                                    size="small"
                                                    error={Boolean(pharmacyTouched.uom && pharmacyErrors.uom)}
                                                  />
                                                )}
                                                isOptionEqualToValue={(option, value) => option.from_uom === value.from_uom}
                                              />
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 120, position: 'relative' }}>
                                              <TextField
                                                data-testid={`package-pharmacy-${idx}-discount-input`}
                                                disabled={isPharmacyDisabled}
                                                sx={{
                                                  ...(isPharmacyDisabled ? {
                                                    backgroundColor: '#fafafa',
                                                    opacity: 0.9,
                                                    transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                                  } : {}),
                                                  '& .MuiFormHelperText-root': {
                                                    position: 'absolute',
                                                    bottom: -18,
                                                    left: 0,
                                                    whiteSpace: 'nowrap',
                                                    margin: 0,
                                                    fontSize: '0.7rem',
                                                  },
                                                }}
                                                fullWidth
                                                size="small"
                                                placeholder={tBilling('package_config.placeholders.enter', { defaultValue: 'Enter' })}
                                                type="number"
                                                name={`pharmacy_items[${idx}].discount_percent`}
                                                value={item.discount_percent}
                                                onChange={handleChange}
                                                slotProps={{ htmlInput: { min: 0, max: 100 } }}
                                                error={Boolean((pharmacyTouched.discount_percent && pharmacyErrors.discount_percent) || isPharmacyDiscountOutOfRange)}
                                                helperText={pharmacyDiscountValidationMessage}
                                              />
                                            </TableCell>
                                            <TableCell align="center">
                                              <IconButton
                                                data-testid={`package-pharmacy-${idx}-delete-button`}
                                                color="error"
                                                onClick={() => {
                                                  handleDeleteClick(() => {
                                                    if (idx === 0 && values.pharmacy_items.length === 1) {
                                                      setFieldValue('pharmacy_items[0].item', '');
                                                      setFieldValue('pharmacy_items[0].item_name', '');
                                                      setFieldValue('pharmacy_items[0].quantity', '');
                                                      setFieldValue('pharmacy_items[0].uom', '');
                                                      setFieldValue('pharmacy_items[0].discount_percent', '');
                                                    } else {
                                                      remove(idx);
                                                    }
                                                  });
                                                }}
                                                sx={{ minWidth: 0, p: 0.5 }}
                                                disabled={isPharmacyDisabled}
                                              >
                                                <DeleteIcon />
                                              </IconButton>
                                            </TableCell>
                                          </>
                                        );
                                      })()}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        );
                      }}
                    </FieldArray>
                  </Box>
                )}

                {/* Summary Section */}
                <Box sx={{ mt: 5, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>{tBilling('package_details.summary')}</Typography>
                  <Grid2 container spacing={2} alignItems="flex-start">
                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      <TextField
                        label={tBilling('package_details.package_amount')}
                        fullWidth
                        disabled
                        size="small"
                        name="summaryPackageAmount"
                        value={values.package_details.package_amount}
                        slotProps={{ input: { readOnly: true } }}
                        sx={
                          { backgroundColor: '#fafafa',
                            opacity: 0.9,
                            transition: 'background-color 0.3s ease, opacity 0.3s ease',}
                        }
                      />
                    </Grid2>

                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      <TextField
                        data-testid="package-net-amount-input"
                        label={tBilling('package_details.net_amount')}
                        fullWidth
                        size="small"
                        name="package_details.package_net_amount"
                        disabled
                        sx={
                          { backgroundColor: '#fafafa',
                            opacity: 0.9,
                            transition: 'background-color 0.3s ease, opacity 0.3s ease',}
                        }
                        value={netAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        slotProps={{ input: { readOnly: true } }}
                      />
                    </Grid2>
                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      <TextField
                        data-testid="package-remark-input"
                        label={tBilling('package_details.remark')}
                        fullWidth
                        disabled={values.package_details.is_finalized || isOpenPackageSelected}
                        sx={(values.package_details.is_finalized || isOpenPackageSelected) ? {
                          backgroundColor: '#fafafa',
                          opacity: 0.9,
                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                        } : undefined}
                        size="small"
                        name="package_details.remark"
                        value={values.package_details.remark || ''}
                        onChange={handleChange}
                        placeholder={tBilling('package_details.enter_remark')}
                      />
                    </Grid2>
                  </Grid2>
                </Box>

                <Box sx={{ mt: 3, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{tBilling('package_details.validations')}</Typography>
                  <Box sx={{ pl: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: isServiceAmountMatch ? 'success.main' : 'error.main',
                        fontWeight: isServiceAmountMatch ? 600 : 500,
                      }}
                    >
                      {tBilling('package_details.validation_service_amount')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: hasMinRows ? 'success.main' : 'error.main',
                        fontWeight: hasMinRows ? 600 : 500,
                      }}
                    >
                      {tBilling('package_details.validation_at_least_one')}
                    </Typography>
                  </Box>
                </Box>


                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    
                  <Button data-testid="package-cancel-button" variant="outlined" color="inherit" sx={{ mr: 2, minWidth: 120, fontWeight: 600, borderRadius: 2 }} onClick={() => { navigate(`/admin/billing/package/patient-package/${patientId}`);}} >
                    {tBilling('package_details.cancel')}
                  </Button>
                  <Button
                    data-testid="package-save-finalize-button"
                    type="button"
                    variant="contained"
                    color="primary"
                    sx={{ minWidth: 120, fontWeight: 600, borderRadius: 2, backgroundColor: allCustomValid && !isSavingPackage ? '#444' : '#9e9e9e', '&:hover': { backgroundColor: allCustomValid && !isSavingPackage ? '#222' : '#9e9e9e' } }}
                    disabled={!allCustomValid || isSavingPackage}
                    onClick={() => {
                      if (!allCustomValid) return;
                      const hasErrors = Object.keys(errors).length > 0;
                      document.querySelector('form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                      if (!hasErrors) {
                        setFieldValue('package_details.is_finalized', true);
                      }
                    }}
                  >
                    {tBilling('counseling_details.save_and_finalize')}
                  </Button>
                  <Button data-testid="package-save-button" type="submit" 
                    variant="contained" 
                    color="primary" sx={{ minWidth: 120, fontWeight: 600, borderRadius: 2, backgroundColor: allCustomValid && !isSavingPackage ? '#444' : '#9e9e9e', '&:hover': { backgroundColor: allCustomValid && !isSavingPackage ? '#222' : '#9e9e9e' } }}
                    disabled={!allCustomValid || isSavingPackage}>
                    {tBilling('counseling_details.save')}
                  </Button>
                </Box>
                <ConfirmationModal
                  open={openModal}
                  handleClose={handleCloseModal}
                  onConfirm={() => handleConfirmedSubmit(values)}
                  type={'save'}
                  title={tBilling('package_details.package_details')}
                  contentMessage={tBilling('package_details.confirm_save_message')}
                  isConfirming={isSavingPackage}
                />
                <ConfirmationModal
                  open={deleteConfirmOpen}
                  handleClose={handleCancelDelete}
                  onConfirm={handleConfirmDelete}
                  type={'delete'}
                  title={tBilling('package_details.delete_confirmation') || 'Delete Confirmation'}
                  contentMessage={tBilling('package_details.confirm_delete_item') || 'Are you sure you want to delete this item?'}
                />
              </Form>
            </Box>
          );
        }}
      </Formik>
    </>
  );
};

export default CreatePackage;
